// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// Everything in `src/xr/`, wired to one machine (M5-01 … M5-29).
//
// The pieces in this directory are deliberately small and deliberately ignorant of each other —
// a press gate knows nothing about a placement, a placement knows nothing about a wrist menu.
// This is the one file that knows about all of them, and its whole job is ordering: read the
// hands, decide where the machine is, turn contacts into controls, and hand the shell a list of
// things that just happened.
//
// **It does not own the frame loop and it does not own the game.** `MachineRuntime` still drives
// the simulation, exactly as it does on a flat screen; what changes in a session is *where the
// frame callback comes from* (the XR session's, not the browser's) and *where the controls come
// from* (the cabinet, not the keyboard). Everything downstream — the trace, the score, the
// validator — cannot tell the difference, which is the property that makes a headset score
// comparable to a desktop one.
//
// **The XR scene is a child of the same scene.** The machine is not rebuilt for XR. It is the
// same `MachineScene`, moved and scaled by the placement, with a venue added around it in VR and
// nothing added around it in MR. Rebuilding it would mean two machines that could disagree, which
// is the defect class this project keeps finding.

import { Vector3, type Group, type Object3D, type Scene, type WebGLRenderer } from 'three'
import { fitCabinetToFloor } from '../world/cabinetFloor'
import { menuStandBottom, quickMenuCentre, followQuick, normalMenuShape, QUICK_MENU_SCALE } from './menuStand'
import { NavigationPointerRelease } from './build/tap'
import type { MachineDefinition, MachineDevice } from '@core/machine'
import type { PlayControls } from '../input/desktop'
import { devicesOf } from '../builder/layoutDraft'
import { BuildMode } from './build/buildMode'
import { buildModeOffered } from './build/flag'
import {
  KeepSession,
  keepDraft,
  keepTitleFor,
  refusedLine,
  type KeepOutcome,
  type KeepPort,
} from './build/keeping'
import { boardFor, carriedOver, missedLine, raisedBoard, type CarryHand } from './build/partCarry'
import { PinchCarry } from './build/pinchCarry'
import type { SnapView } from './build/snapView'
import type { ValidationView } from './build/validationView'
import type { InspectorView } from './build/inspector'
import type { MiniMapView } from './build/miniMap'
import type { PointsView } from './build/rampPoints'
import { TestTools, speedText } from './build/testTools'
import { benchText, type BenchLanguage } from './build/benchStrings'
import { Haptics, SNAP_PULSE } from './haptics'
import { benchFrom, headRelative } from './build/testFlip'
import { binRest, buildPartsTray, type PartsTrayBuild, type TrayHand, type TrayPlinth } from './build/partsTray'
import type { CabinetDimensions } from '../world/cabinet'
import { buildArcadeRoom, type ArcadeRoomBuild } from '../world/arcadeRoom'
import { buildWristPanel, TOWARD_VIEWER, type WristPanelBuild } from '../world/wristPanel'
import type { GalleryDetail, GalleryDetailRequest, MachineGallery } from '../world/machineGallery'
import { buildGhostHands, type GhostHandsBuild } from '../world/ghostHands'
import { buildPinchMarks, type PinchMarksBuild } from '../world/pinchMarks'
import { ControlPicture } from '../world/controlPicture'
import { HAND_JOINT_COUNT, HandJointsReader, type HandJoints } from './handJoints'
import { buildNoticePanel, type NoticePanelBuild } from '../world/noticePanel'
import { buildReticle, type ReticleBuild } from '../world/placementReticle'
import { NOTICE_TOAST_SECONDS } from './sessionNotice'
import { buildAvatars, type AvatarsBuild } from '../world/avatars'
import type { AvatarPose } from '../net/poseStream'
import { toPlayfield, toVenue, yawToPlayfield, yawToVenue, type VenuePlacement } from '../net/venueFrame'
import type { VenuePort } from './venuePort'
import { UNMUTED, type VoiceState } from '../audio/voiceMute'
import type { AudioLevel } from '../audio/audioPrefs'
import { ArcadeFloor, bayLookedAt } from './arcadeBays'
import { baySounds, type BaySound } from '../audio/bayAmbience'
import {
  ARCADE_FULL_LINE,
  arrivalLine,
  bayMove,
  bayRowLabel,
  bayStatuses,
  movedByRoomLine,
  roomVerdict,
  takenLine,
  type BayOccupant,
  type BayStatus,
} from './bayNavigation'
import { CabinetRig, type BoundControls, type ConfirmEvent } from './cabinetRig'
import { believableFloor, SeatedDetector, seatedPlacement } from './comfort'
import {
  cycleFlashing,
  cycleHeightOffset,
  cycleMotionPreference,
  flashingLabel,
  heightDropOffset,
  heightLabel,
  motionLabel,
  motionStepOf,
  readHeightOffset,
  readSeatedPlay,
  writeSeatedPlay,
} from '../client/comfortPrefs'
import { readFlashingChoice, readMotionIntensity } from '../client/accessibility'
import { knownEyeHeight, loadEyeHeights } from './eyeHeightMemory'
import type { ImmersiveMode } from './features'
import { wireInputMethod } from './inputMethod'
import type { InputMethod as WireInputMethod } from '../net/contract'
import { lightingMatchFor, type LightEstimate, type LightingMatch } from './lightingMatch'
import { defaultPlacement, toCabinetPoint, toRoomPoint, type MutablePoint, type Placement } from './placement'
import {
  forwardFromYaw,
  levelForward,
  placementInFrontOf,
  squareToRoom,
  SurfaceSource,
  withinReach,
  type LevelDirection,
  type ProbeState,
} from './viewerRay'
import {
  FloorAlignment,
  type FloorRendererPort,
  type FloorSessionPort,
  type OffsetFactory,
} from './floorAlignment'
import {
  AIM_TOO_HIGH_LINE,
  NUDGE_RADIANS,
  PlacementRitual,
  type RitualView,
} from './placementRitual'
import {
  createAnchorAt,
  doubtsAboutRestore,
  forgetAnchor,
  placementFromAnchor,
  releaseAnchor,
  rememberedHandle,
  rememberPlacement,
  restoreAnchor,
  type RestoreOutcome,
  type XRAnchorFramePort,
  type XRAnchorPort,
  type XRAnchorSessionPort,
} from './anchors'
import { safeStorage, type StorageLike } from '../storage/local'
import {
  XRSessionManager,
  type XRRendererPort,
  type XRSessionPort,
  type XRSystemPort,
} from './session'
import { SessionSafety, recycleWarning, type SafetyAction } from './sessionSafety'
import { contextRows, unavailableReason, bayOfItem, PALM_UP_EXIT, WristMenu, type MenuItem, type MenuPage, type MenuPresentation, type MenuSelection, type MenuState, type SwitchTarget } from './wristMenu'
import { PalmSession } from './palmSession'
import { lookingAt, palmFacing, palmIsOpen, palmWithinReach } from './palmReach'
import { onAControl } from './cabinetControls'
import { FloorPlaneWatch } from './floorPlane'
import { HandTracker, type HandReport, type XRFramePort, type XRInputSourcePort } from './xrInput'

/**
 * What the player sees around the machine: their own room through the passthrough, or the arcade
 * (15 Sep 2026). Not the session's mode: VR is a room drawn inside the passthrough session where
 * the headset has one, so the look can change without the session ending. See `XRRuntime.setLook`.
 */
export type Look = 'mr' | 'vr'

/** What the shell needs to draw and say, refreshed every frame. */
export interface XRFrameReport {
  readonly presenting: boolean
  readonly mode: ImmersiveMode | undefined
  /** The look being presented, or undefined outside a session. */
  readonly look: Look | undefined
  /**
   * Which scheme is live, as the wire spells it (M9-38).
   *
   * On the report rather than fetched from the rig because the host has to *count* it: a game
   * played half on hands and half on controllers is one row on a leaderboard, and the row has to
   * say which. `machineRuntime` tallies presenting frames per method and writes the larger into
   * the trace — which for four milestones said `keyboard` for every game ever played in a headset.
   */
  readonly method: WireInputMethod
  /** Confirmations raised this frame — sounds, haptics and button depressions. */
  readonly confirmations: readonly ConfirmEvent[]
  /** A one-frame sentence: an input-method switch, a recycle warning, a room-fit note. */
  readonly toast: string | undefined
  readonly ritual: RitualView | undefined
  // `menuOpen: boolean` lived here until M9-53's close and nothing ever read it — the identical
  // shape as `lighting`, in the identical struct, and the resolution went the other way: `lighting`
  // carried three numbers a whole feature hung off, while this duplicated `menu.open` into a report
  // for a consumer nobody has named. A copy of a state is a second thing that can be wrong about
  // it; whoever needs "is the menu open" adds the field back with themselves attached.
  readonly lighting: LightingMatch
  /** True when the session is hidden — headset off, or the system menu is up. */
  readonly paused: boolean
}

export interface XRRuntimeOptions {
  readonly renderer: WebGLRenderer
  readonly scene: Scene
  /** The machine's own group, so the placement can move it without rebuilding it. */
  readonly machine: Object3D
  readonly dimensions: CabinetDimensions
  readonly xr?: XRSystemPort | undefined
  /**
   * The XR-030 recycle watchdog (`sessionSafety.ts`), or not. `recycle` decides; the two numbers
   * are honoured only when it is on. Left out entirely, the watchdog runs at its own defaults —
   * which is what the tests drive, and what `safetyFlag.ts` decides for the product.
   */
  readonly recycle?: boolean
  readonly recycleAfterSeconds?: number
  readonly recycleWarnSeconds?: number
  /**
   * The live `XRSession`, for the two things that need it directly: restoring an anchor and
   * creating one. A function rather than a value because the session does not exist when this is
   * constructed and is a different object every time the player enters.
   */
  readonly session?: () => unknown
  /**
   * How a height correction becomes a rigid transform (M5-33).
   *
   * `XRRigidTransform` in a browser and a plain object under Vitest, which is why it is injected
   * at all: the DOM constructor does not exist in Node and a module that reached for it directly
   * would take the whole XR directory out of the test suite.
   */
  readonly rigidTransform?: OffsetFactory
  /** Where the remembered placement lives. Defaults to `localStorage` where there is one. */
  readonly storage?: StorageLike | undefined
  /** Told when the player picks something from the wrist menu. The shell performs it. */
  readonly onMenu?: (selection: MenuSelection) => void
  /** Told the session ended, and whether the product asked for it. */
  readonly onEnd?: (mode: ImmersiveMode, deliberate: boolean) => void
  /**
   * Build mode's seam into the game (M13-10, ADR 0015).
   *
   * Absent, and there is no Build mode whatever the flag says — this class owns the hands, the
   * draft and the bench, and the machine is the owner's to rebuild. `MachineRuntime` supplies it.
   */
  readonly build?: BuildHost
}

/** What Build mode needs from whoever owns the game (M13-10). */
/** The Builder's test tools, as the host that owns the session applies them (M13-25). */
export interface TestToolsPort {
  /** The draft is under test: the tools are allowed, the trail records from now. */
  readonly begin: () => void
  /** Back to build: the tools are refused, the clock runs, the ribbon comes down. */
  readonly end: () => void
  /** Whether the simulation is stopped. */
  readonly frozen: () => boolean
  /** Stop, or run again. */
  readonly freeze: () => void
  /** Advance one millisecond. True when it will. */
  readonly step: () => boolean
  /** How fast time runs, 0.1 to 1. */
  readonly slow: (scale: number) => void
  /** Move the ball in play to a point on the playfield, at rest. False with none in play. */
  readonly placeBall: (x: number, y: number) => boolean
  /**
   * Put one more ball on the playfield at a point, at rest (26 Sep 2026, "a second ball in a
   * test"). False when the game is not in play or every ball the machine has is already out.
   */
  readonly addBall: (x: number, y: number) => boolean
  /** Draw the trajectory ribbon, or not. */
  readonly ribbon: (on: boolean) => void
}

export interface BuildHost {
  /** The machine as the session runs it now. The draft opens on this. */
  readonly definition: () => MachineDefinition
  /**
   * The language the player reads in (M13-26): the bench's tiles, rows, labels and toasts are
   * worded in it. Read every frame, because the choice is the player's and can change under
   * the session; English when the host does not say.
   */
  readonly language?: () => BenchLanguage
  /** The playfield's own group (PH-001 axes), for `worldToLocal` and the ghost. */
  readonly playfield: () => Object3D
  /** The mode is on: remember the machine as it is, and score nothing until `leave`. */
  readonly enter: () => void
  /**
   * The draft changed: make the session run it and redraw what changed. `edited` is whether the
   * bench now differs from the machine it entered on (`canUndo`) — the Builder's gate on writing
   * a draft (M13-16), handed along so the shell can keep the same one.
   */
  readonly adopt: (definition: MachineDefinition, edited: boolean) => void
  /** Draw, or take down, the ghost of the part in hand. */
  readonly ghost: (
    device: MachineDevice | import('../builder/parts').FlasherPart | undefined,
    /** A placed part in hand off the board rides the hand: playfield metres, and height over the plane (24 Oct 2026). */
    shift?: readonly [number, number, number],
  ) => void
  /**
   * Hide the drawn copy of the placed part in hand while it is carried, or show it again
   * (undefined): the ghost is the part, and the copy left where it was read as a duplicate
   * (the 1.0.103 headset round, 26 Sep 2026).
   */
  readonly lift?: (deviceId: string | undefined) => void
  /** Drop the part in hand into the remove tray: it falls to `floorUnder`, rests and fades (24 Oct 2026). */
  readonly dropGhost?: (floorUnder: (lowest: Vector3) => Vector3) => boolean
  /** One frame of the parts dropped into the remove tray; `clear` takes them all away. */
  readonly advanceDrops?: (seconds: number, clear?: boolean) => void
  /** Light the placed part under a fingertip — the one a pinch would take — or take the halo down. */
  readonly highlight?: (deviceId: string | undefined) => void
  /**
   * Draw what the snap looks like — the guides, the marks, the reticle, the label — or take it
   * all down (M13-13). `flash` is true on the frame a rule changed; `seconds` is the frame's.
   */
  readonly guides?: (view: SnapView | undefined, seconds: number, flash: boolean) => void
  /**
   * The click half of the sound-and-light twin (M13-13): a snap rule caught the part in a
   * *hand*, at this point on the playfield. A controller gets `SNAP_PULSE` instead, from here.
   */
  readonly click?: (x: number, y: number) => void
  /**
   * The report on the machine (M13-17): the reachability decal, a callout over every part a
   * complaint names, a flipper pair's measurement — or take it all down. `scale` is the bench's,
   * so a callout is a fingertip in the room whatever the bench is drawn at.
   */
  readonly validation?: (view: ValidationView | undefined, scale: number) => void
  /**
   * The inspector card beside the selected part (M13-22): its rows as rails and steppers, the
   * values as words — or take it down. `scale` is the bench's, so the card is a hand wide in
   * the room whatever the bench is drawn at, and every target on it sixteen millimetres.
   */
  readonly inspector?: (view: InspectorView | undefined, scale: number, held?: string) => void
  /**
   * The mini-map beside the bench (M13-23): the plan painted by the Builder's own `paint()`, the
   * two nudge pads under it — or take it down. `scale` is the bench's, so the map is a hand wide
   * in the room whatever the bench is drawn at, and every arrow sixteen millimetres.
   */
  readonly map?: (view: MiniMapView | undefined, scale: number) => void
  /**
   * The selected ramp's control points (M13-24): a handle at each, the one under a fingertip
   * lit, and while one is in hand the curve as it is being shaped — or take them down. `scale`
   * is the bench's, so a handle is a fingertip in the room whatever the bench is drawn at.
   */
  readonly points?: (view: PointsView | undefined, scale: number) => void
  /**
   * The Builder's test tools on the draft's session (M13-25), allowed by the host only between
   * `begin` and `end` — while the draft is under Test play — and never on a scored game.
   */
  readonly tools?: TestToolsPort
  /** The mode is over: the original machine, back. */
  readonly leave: () => void
  /**
   * How a Keep reaches the arcade (M13-20): the API client and whether there is an account to
   * keep into, answered at the press. Absent is a build with no backend, and the row says so.
   * `src/xr` is on the anonymous side of FR-030 and cannot name the client; the shell answers
   * this through `import('../account')`, the way it applies a mute.
   */
  readonly keep?: KeepPort
  /**
   * Was this session asked for *in order to build* (`build/intent.ts`)? Read once, as the session
   * starts; a true answer enters Build mode on the first frame that has a head, so an author who
   * came through the Builder's door lands at the bench and not in a game. Absent means never.
   */
  readonly wanted?: () => boolean
}

/** How long a tapped callout keeps its part lit, seconds (M13-17): long enough to look, short enough to move on. */
export const CALLOUT_LIT_S = 2

/**
 * Wrist rows that move the machine, refused while Build mode is on (06 §9's standing rule).
 *
 * Refused with a sentence rather than hidden, for Phase 0; M13-15 takes them off the root page
 * while building, which is the rule this repository prefers ("a row that cannot act must not be
 * offered").
 */
const MOVES_THE_MACHINE: ReadonlySet<MenuItem> = new Set<MenuItem>([
  'seatedMode',
  'heightOffset',
  'scaleMode',
  'faceMe',
  'recenterMachine',
  'nudgeLeft',
  'nudgeRight',
  'replaceMachine',
  'forgetSpot',
])

/** Wrist rows that leave the machine, and so end Build mode first. */
const LEAVES_THE_MACHINE: ReadonlySet<MenuItem> = new Set<MenuItem>(['exitXr', 'otherMachine'])

/**
 * The smallest eye-height step across a reset that is read as the floor having moved, metres, and
 * the largest one believed (15 Sep 2026). Under the first is posture; over the second is a reading
 * from a headset in somebody's hands, not on their head.
 */
export const EYE_CONTINUITY_MIN = 0.25
export const EYE_CONTINUITY_MAX = 1.5

/**
 * How long after a `reset` the head must have been quiet before the machine is re-stood, seconds
 * — the native client's `SettleSeconds` (06 §3.2). A long-press arrives as two or three resets
 * with the head at invented heights between them.
 */
export const RECENTRE_SETTLE_S = 0.75
/** The longest a recentre waits for a believable, still head before answering with what it has. */
export const RECENTRE_SETTLE_MAX_S = 3
/** A head that moved less than this between frames is still: vertically, and on the floor plane. */
export const RECENTRE_STILL_Y = 0.03
export const RECENTRE_STILL_XZ = 0.05

/**
 * A frame longer than this is a stall — the headset off, the system UI over the page — and the
 * settle clock restarts rather than counting the stall as waiting.
 */
export const RECENTRE_STALL_S = 0.5

/** A floor move this large is said to the player; smaller ones are only in the console. */
export const FLOOR_SAID_FROM = 0.1
export const FLOOR_FROM_THE_ROOM = 'Floor corrected from the room scan.'

/** Said by Bring it to you, whose answer comes once the head is up and still. */
export const LOOK_WHERE_YOU_WANT_IT = 'Look where you want it — hold still and it comes to you.'

/** The two things a caller has to supply per frame that only WebXR knows. */
export interface XRFrameInput {
  readonly frame: XRFramePort
  readonly referenceSpace: unknown
  readonly inputSources: Iterable<XRInputSourcePort>
  /**
   * The viewer's pose in the reference space. Drives seated detection, Face Me and the pose
   * stream — `pitch` and `roll` are read only by the last of those, which is why they are
   * optional: every existing caller predates M8-03 and a head that does not nod is a smaller loss
   * than a signature nobody can satisfy.
   */
  readonly head:
    | {
        x: number
        y: number
        z: number
        yaw: number
        pitch?: number
        roll?: number
        /**
         * The viewer's forward direction, unit length (M5-33).
         *
         * Optional for the same reason `pitch` is: every caller written before M5-33 supplies a
         * yaw and nothing else, and a head with no direction places the machine the way it always
         * did rather than failing to compile. Where it *is* supplied, it is preferred over the
         * yaw — a vector has no convention to disagree about.
         */
        forward?: { x: number; y: number; z: number }
      }
    | undefined
  /** Where the hit test landed, MR only. */
  readonly surface?: { x: number; y: number; z: number; yaw?: number } | undefined
  /**
   * Whether the runtime's surface probe is going to answer at all, MR only (M5-33).
   *
   * Absent is read as `pending`, which is the safe reading: it waits for a real answer rather
   * than substituting one. Only the browser frame loop knows this, and only the ritual needs it.
   */
  readonly surfaceProbe?: ProbeState | undefined
  /** The runtime's light estimate, MR only and only when granted. */
  readonly light?: LightEstimate | undefined
  /** `session.visibilityState === 'visible'`. */
  readonly visible: boolean
  /** Left palm's normal dotted with world up, or undefined when no left hand is tracked. */
  readonly palmUp: number | undefined
  /**
   * The left palm's normal, when the runtime gives one (the 1.0.110 round): with the head, it
   * says how squarely the palm faces the player (`palmFacing`), which is what opens and closes
   * the menu. Absent, the menu reads `palmUp` as before.
   */
  readonly palmNormal?: { readonly x: number; readonly y: number; readonly z: number } | undefined
  /**
   * The room scan's floor under the player, as a height in the presented space
   * (`floorPlane.ts`); absent on a runtime without planes or a frame without a floor.
   */
  readonly floorY?: number
  /** Either grip button, for the controller way of opening the menu. */
  readonly gripHeld: boolean
  /**
   * The session's own pinch per hand — `selectstart`/`selectend` for a hand input source — or
   * nothing for a hand those events have never named (`xrFrameLoop.ts`, 13 Sep 2026). The hand
   * tracker takes this over its thumb-to-index rule when it is there.
   */
  readonly pinch?: { readonly left?: boolean; readonly right?: boolean }
  /** Comfort-mode bindings, when the player has turned it on. */
  readonly bound?: BoundControls
  /**
   * Both wrists, for the pose stream (M8-04). `null` for a hand that is not tracked — which is
   * not the same as a hand at the origin, and the difference is a hand inside the cabinet.
   */
  readonly wrists?:
    | { readonly left: AvatarPose | null; readonly right: AvatarPose | null }
    | undefined
}

const NO_CONFIRMATIONS: readonly ConfirmEvent[] = []

export class XRRuntime {
  readonly manager: XRSessionManager
  readonly hands = new HandTracker()
  /** Every joint of both hands, for the picture of them (`ghostHands.ts`). */
  private readonly joints = new HandJointsReader()
  /** The player's own hands, drawn. Built on the first frame that has a hand to draw. */
  private ghost: GhostHandsBuild | undefined
  /** A dot at each hand's pinch point while the room hides the real hands (M13-28). */
  private marks: PinchMarksBuild | undefined
  readonly rig: CabinetRig
  readonly menu = new WristMenu()
  readonly floor = new ArcadeFloor()
  readonly seated = new SeatedDetector()

  /**
   * The other people in the arcade (M8-04, M8-05).
   *
   * **A port rather than a connection, and that is FR-030.** This module is on the front door, so
   * it may not import anything that can reach the network; the account subtree hands one of these
   * over when there is a signed-in player to hand it for. Absent is the normal case and the
   * default — an anonymous visitor and an offline build both get an arcade with nobody in it,
   * which is the truth.
   *
   * Settable rather than a constructor option because joining is asynchronous and entering a
   * headset is not: the session starts, the room fills a moment later, and a player who signs in
   * mid-session should not have to leave and come back.
   */
  venuePresence: VenuePort | undefined

  /**
   * What the player's mute controls currently say (M8-09).
   *
   * **Read, never written.** The two mute rows are drawn from this and their selections go out to
   * the shell like every other row that changes something this class does not own — the same rule
   * that keeps pausing and the mixer out of here. A copy that this object could set would be a
   * second owner of the one control in the product whose disagreement with reality is a privacy
   * failure rather than an annoyance.
   *
   * Settable from outside for the same reason `venuePresence` is: a player can mute themselves
   * from the HUD before a session starts and from the wrist menu during one, and the state
   * outlives both.
   */
  voice: VoiceState = { mute: UNMUTED, access: 'unknown', noticed: false }

  /**
   * The machine's own sound is off (07 §7).
   *
   * Settable from outside for the same reason `voice` is, and pushed by the same owner: the
   * player can mute from the HUD before a session starts and from this menu during one, and
   * `MachineRuntime` holds the one copy either way. A second copy here would be a wrist row
   * and a HUD button disagreeing about whether the machine is making a noise.
   */
  audioMuted = false

  /**
   * How loud everything is, and whether callouts are captioned (07 §7, NFR-022, 06 §5).
   *
   * Pushed from `MachineRuntime` beside `audioMuted` and for its reason: the Sound page's rows
   * state these conditions, `MachineRuntime` holds the one copy of each, and a copy kept here
   * would be a wrist row and a settings page disagreeing about a machine they both describe.
   *
   * Defaulted to the values `AUDIO_DEFAULTS` gives a player who has never touched either, so a
   * session that somehow starts before the push draws the truth about a fresh install rather than
   * a silent machine with its captions off.
   */
  audioMaster: AudioLevel = 'full'

  audioCaptions = true

  /**
   * What the machine is saying, captioned (M9-47, NFR-022).
   *
   * **The second producer of the notice panel, and the reason it exists.** NFR-022 asks for every
   * spoken callout to be captioned *"on screen **and in the headset**"*, and the browser did the
   * first half only: the caption reaches `MachineStage`'s DOM, which is not composited in an
   * immersive session. Set every frame by `machineRuntime` from its own readout, the same way
   * `audioMuted` and `audioMaster` are — the audio layer owns how long a caption lasts, so this is
   * a value that arrives and leaves rather than an event this class has to hold.
   *
   * Yields to `toast`: a recycle countdown and a lost anchor are things the player has to act on,
   * and a caption is a thing they are welcome to miss.
   */
  caption: string | undefined

  /**
   * The simulation is stopped because somebody stopped it (M3-55).
   *
   * Distinct from `pausedByVisibility`, which is the headset's system menu taking the frame
   * loop away. The wrist menu's Pause row used to read *that* one — a state only ever true
   * while the panel is invisible — so it said "Pause" over a machine the player had just
   * paused from that row, and could never say "Resume" to anybody who could read it.
   */
  gamePaused = false
  gameInProgress = true
  private pendingDeparture: { selection: MenuSelection; button: boolean; saving: MachineDefinition | undefined } | undefined
  /**
   * The machine is asking for three characters (M2-19a).
   *
   * Pushed in from `machineRuntime` the way `gamePaused` is, and for the same reason: this class
   * runs the session and does not own the game. It decides whether the wrist menu offers the row
   * that declines the high-score table.
   */
  enteringInitials = false

  /** The MR placement ritual. Rebuilt on entry, because the room may have changed. */
  private ritual: PlacementRitual | undefined
  /** The aiming visual for the ritual above. Exists only while a session could draw one. */
  private reticleDrawn: ReticleBuild | undefined
  /** Where the reticle's answers come from when the room has none. Rebuilt with the ritual. */
  private surfaces: SurfaceSource | undefined
  /** The runtime's floor, re-read on entry and on every platform recentre (M5-33). */
  private floorSpace: FloorAlignment | undefined
  /** The session's `local` space — the origin that does not move — for `holdTheFloor`. */
  private localSpace: unknown
  /**
   * Frames to wait before measuring the player against a newly installed space.
   *
   * The viewer's pose is reported in whichever space the frame began in, so a height read on the
   * frame a space is installed measures the player against the space that was just replaced —
   * and corrects for an error that is no longer there. One frame is enough; two is the same
   * answer and survives a renderer that installs on its own schedule.
   */
  private alignIn = 0
  /** The space whose `reset` we are subscribed to, so re-entry never double-subscribes. */
  private resetSpace: { removeEventListener?: (t: string, l: () => void) => void } | undefined
  private safety: SessionSafety | undefined
  private venue: ArcadeRoomBuild | undefined
  /**
   * Where the venue's own origin stands in the reference space, and how the room is turned
   * (`Placement`'s yaw convention; `venueFrame.ts` does the arithmetic). Written by `standAtBay`.
   */
  private venuePose: VenuePlacement = { x: 0, y: 0, z: 0, yaw: 0 }
  /**
   * Where the machine stands, from the head — kept every frame so that a reset can leave it
   * exactly there (`keepAcrossReset`, 15 Sep 2026). While building, the placement Leave build
   * returns to; the bench is head-relative by construction.
   */
  private headRelation: HeadRelation | undefined
  /**
   * The machine under test, relative to the head (25 Sep 2026): what a reset at the machine during
   * Test play keeps. `headRelation` is the placement Leave build returns to, which is not the one
   * a hand is pulling the plunger on.
   */
  private testRelation: HeadRelation | undefined
  /** A test placement kept across a reset, applied where the bench is re-derived. */
  private testKept: Placement | undefined
  /** The eye height last read with a hand in view: a headset being worn, not held (15 Sep 2026). */
  private wornEye: number | undefined
  /** `wornEye` as it was when the reset arrived, for `floorAcrossReset`. */
  private eyeBeforeReset: number | undefined
  /**
   * Whether any reset of the pending burst arrived with a hand at the machine (15 Sep 2026): the
   * runtime's own recentre, fired by a pinch held on the plunger, rather than one the player made
   * with a hand raised. See `onReferenceSpaceReset`.
   */
  private resetAtTheMachine = false
  private avatars: AvatarsBuild | undefined
  private panel: WristPanelBuild | undefined
  private lighting: LightingMatch = lightingMatchFor(undefined)
  private comfortOn = false
  private seatingChoiceKnown = false
  private pausedByVisibility = false

  /** Whether the player has already been told the surface they are aiming at is too high. */
  private saidTheAimIsTooHigh = false
  private elapsed = 0
  private toast: string | undefined
  /** Said once per spell of it, so the line does not reprint at 72 Hz (M8-20). */
  private toldArcadeFull = false
  /** The room as it last sounded, and the bay-and-occupancy it was computed for (M8-21). */
  private room: readonly BaySound[] | undefined
  private roomKey = -1
  /** The bay this client has asked the room for, and when. See `reconcileBay` (M8-20). */
  private askedBay: number | undefined
  private askedAt = 0
  /**
   * Build mode (M13, ADR 0015): the state machine and, while it is on, the one owner of what a
   * hand is for. Idle — and transparent — unless `options.build` is supplied and the flag is on.
   */
  readonly build = new BuildMode()
  /** The parts tray, built on entry and torn down on leave. */
  private tray: PartsTrayBuild | undefined
  /**
   * Where the machine stood before Build mode, to go back to.
   *
   * Every placement the ordinary paths would have written while the mode is on lands here instead
   * (`placeMachine`), so leaving puts the machine where the ritual, the anchor or the bay would
   * have had it — including after a recycle renumbered the space.
   */
  private buildReturn: Placement | undefined
  /** The tracking space was renumbered under the bench (a recycle): re-derive it from the next head. */
  private benchStale = false
  private readonly buildOffered: boolean
  /** The machine this Build session keeps into (M13-20): opened with the mode, forgotten with it. */
  private keep: KeepSession | undefined
  /** A Keep is on the wire; a second press is told to wait rather than making a second machine. */
  private keeping = false
  /**
   * The Keep that finished since last frame, said once, on the frame. A sentence written from the
   * promise would land between frames, and `advance` clears the toast at the top of every one.
   */
  private keptSaid: KeepOutcome | undefined
  /** The draft on the wire for the Keep in flight, and the last draft a Keep landed for (M13-15). */
  private keepingDefinition: MachineDefinition | undefined
  private keptDefinition: MachineDefinition | undefined
  /**
   * Which look the wrist can switch to — the other one, in a passthrough session (14–15 Sep 2026).
   *
   * Undefined in an `immersive-vr` session, which has no passthrough to switch to, and between
   * sessions. Set with `lookNow` at the start and on every switch.
   */
  private switchTo: SwitchTarget | undefined
  /** The look being presented (`Look`), and the one the next session was asked for. */
  private lookNow: Look = 'mr'
  private lookWanted: Look = 'mr'
  /**
   * The Builder's door was the way in: enter Build mode on the first frame with a head.
   *
   * Asked of the host on the session's first frame (`buildAsked`) and spent on the first frame
   * that has a head, because `enterBuild` derives the bench from one. Both reset with the session,
   * so a head that never arrives does not leave an intent behind for the next one, and a recycle
   * (XR-030), which restarts the session with the mode still on, asks and is told no.
   */
  private buildWanted = false
  private buildAsked = false
  /** Scratch for `worldToLocal`, so a frame allocates nothing. */
  private readonly scratch = new Vector3()
  /** Frame times while editing, for the development heartbeat M13-11 reads. Milliseconds. */
  private readonly buildFrames: number[] = []
  private buildHeartbeatAt = 0
  /** Latched, so one pinch is one teleport rather than one per frame. */
  private pinchWasDown = false
  /** The same latch for the menu's own pinch, which is a different hand and a different gesture. */
  private menuPinchWasDown = false
  private menuTouchAcceptedUntil = Number.NEGATIVE_INFINITY
  /** The row the pointing fingertip was last over, for the console line that says so once. */
  private menuOverWas: MenuItem | undefined

  private readonly options: XRRuntimeOptions
  private readonly storage: StorageLike | undefined
  /** True once the current placement has been written down, so it is written once per placement. */
  private remembered = false
  /** The anchor the runtime gave back, waiting for a frame that can say where it is. */
  private pending: RestoreOutcome | undefined
  /** How many frames the restored anchor has failed to locate. */
  private unlocatedFrames = 0
  /** The anchor the current placement is pinned to, if any. */
  private anchor: XRAnchorPort | undefined
  /**
   * The most recent head pose, for the menu rows that need to know where the player is.
   *
   * `drawMenu` runs from the frame loop but takes no frame: the panel follows a wrist joint the
   * hand tracker already has. "Face me" is the one row that needs the *head*, so the head is kept
   * rather than the signature widened for one caller.
   */
  /**
   * Where the head was on the last frame, including which way it was facing.
   *
   * **`yaw` joined this in G2-02 and its absence was hiding a defect.** The field was `x, y, z`,
   * which is everything the seated *detector* needs — it asks whether somebody has been stationary
   * — and one field short of what the seated *placement* needs, which is where to put a machine in
   * front of them. So `reposition` could not have been called from anywhere holding this, and it
   * was not called from anywhere at all: see `setComfort`.
   */
  private lastHead:
    | NonNullable<XRFrameInput['head']>
    | undefined

  /**
   * The camera-following notice panel, built the first time there is something to say (M9-47).
   *
   * Lazily, like the wrist panel above it and for the same reason: a session that never says
   * anything never allocates a canvas, a texture or a quad. Most sessions say something within a
   * minute — a placement instruction, at least — so this is about the shape of the code rather
   * than about the allocation.
   */
  private notice: NoticePanelBuild | undefined

  /** Seconds a one-frame `toast` still has left on the panel. See `NOTICE_TOAST_SECONDS`. */
  private noticeLeftSeconds = 0

  /** What the panel is holding while `noticeLeftSeconds` runs down. */
  private noticeHolding: string | undefined


  constructor(options: XRRuntimeOptions) {
    this.options = options
    this.storage = options.storage === undefined ? safeStorage() : options.storage
    const seated = readSeatedPlay(this.storage)
    this.comfortOn = seated === true
    this.seatingChoiceKnown = seated !== undefined
    if (seated !== undefined) this.seated.accepted()
    this.buildOffered = options.build !== undefined && buildModeOffered()
    this.rig = new CabinetRig({
      dimensions: options.dimensions,
      placement: defaultPlacement(),
    })
    this.manager = new XRSessionManager({
      xr: options.xr,
      // Three's `WebXRManager` already has `enabled`, `setSession` and `setReferenceSpaceType`,
      // so this satisfies `XRRendererPort` in every way except the nominal type of the session
      // argument — ours is the narrow port the manager is written against and Three's is the DOM
      // `XRSession`. One cast at the boundary, rather than importing the DOM XR types into every
      // module in this directory and losing the ability to test any of them in Node.
      renderer: options.renderer as unknown as XRRendererPort,
      events: {
        onStart: (mode, granted) => {
          const blend = this.manager.current?.environmentBlendMode ?? 'unknown'
          this.say(`[xr] ${mode} started; blend ${blend}; granted: ${[...granted].join(', ') || 'nothing optional'}`)
          this.onStart(mode)
        },
        onEnd: (mode, deliberate) => this.onEnd(mode, deliberate),
      },
    })
  }

  get presenting(): boolean {
    return this.manager.presenting
  }

  /** The mode being presented, or undefined. */
  get mode(): ImmersiveMode | undefined {
    return this.manager.mode
  }

  /** Which look the wrist's Switch row goes to, or undefined while there is no row (14 Sep 2026). */
  get switchOffered(): SwitchTarget | undefined {
    return this.switchTo
  }

  /** The look being presented, or undefined outside a session. */
  get look(): Look | undefined {
    return this.manager.presenting ? this.lookNow : undefined
  }

  /**
   * Change what surrounds the machine, in place (15 Sep 2026, the owner's decision) — the wrist's
   * Switch row.
   *
   * **VR is a room drawn inside the passthrough session.** The row used to end the session and
   * ask for the other mode, and the browser refused every request that did not come within a few
   * seconds of a page click — a headset's `select` is not a user activation on this browser — so
   * an in-session switch was never going to restart a session. Instead, in an `immersive-ar`
   * session the arcade room goes up (a closed box: nothing of the passthrough shows through it)
   * or comes down, the machine standing where each look stands it: the venue's bay at the settled
   * head, or the room's own anchored spot. The game, a Build draft and the anchor all survive,
   * because nothing ended. The native client will do the same with its passthrough layer
   * (Phase 3). An `immersive-vr` session has no passthrough and one look; the row is not drawn.
   */
  setLook(look: Look): void {
    if (this.manager.mode !== 'immersive-ar' || look === this.lookNow) return
    this.lookNow = look
    this.switchTo = look === 'mr' ? 'vr' : 'mr'
    // Reaching stops mid-gesture, as it does when comfort mode switches.
    this.letGoOfEverything()
    // **The machine stays where it stands** (01:37 round: "MR to VR mode worked but machine not
    // in same place"). The room goes up around the machine — the venue is carried so that the
    // player's bay is the machine's own spot — and comes down leaving it there; a remembered
    // anchor, restored on the way back, is that same spot. Only a session's first VR look stands
    // the venue at the head.
    const kept: Placement = { ...((this.build.active ? this.buildReturn : undefined) ?? this.rig.placement) }
    if (look === 'vr') {
      this.startVrLook()
      this.recentreWhy = undefined
      this.standVenueAt(kept)
    } else {
      this.startMrLook()
      this.ritual?.adopt(kept)
      this.placeMachine(kept)
    }
    this.say(`[xr] look: ${look === 'vr' ? 'the arcade room is up — VR inside the passthrough session' : 'the arcade room is down — passthrough'}`)
    this.toast = look === 'vr' ? 'Switched to VR.' : 'Switched to MR.'
  }

  /** Is comfort mode on? The wrist menu toggles it; the seated detector offers it. */
  get comfort(): boolean {
    return this.comfortOn
  }

  /**
   * Turn seated comfort mode on or off (XR-007, 06 §6).
   *
   * ***The machine did not move.*** XR-007's second bullet is "the machine repositions to a
   * comfortable seated height and angle", `seatedPlacement` implements it in full — the stand-off
   * derived from the cabinet's own depth, the drop chosen by posture, the yaw convention that
   * differs between a head and a cabinet — and `reposition` is the method that applies it. Until
   * G2-02 `reposition` had **no caller anywhere in the product**: `comfortOn` gated the offer and
   * chose the control bindings, and a player who switched seated mode on got their controls rebound
   * and a machine exactly where it had been.
   *
   * This is the class of defect this repository has recorded a dozen times — complete, tested work
   * connected to nothing — and it is invisible from the outside in the worst way, because the half
   * that *is* wired makes the row feel like it did something.
   *
   * Only on the way on. Turning comfort off does not put the machine back: the player has since
   * been looking at it where it is, and moving a cabinet out from under somebody who pressed a
   * control about their controls is a second surprise for one decision.
   */
  setComfort(on: boolean): void {
    if(this.build.active)return
    this.comfortOn = on
    this.seatingChoiceKnown = true
    writeSeatedPlay(on, this.storage)
    this.seated.accepted()
    if (on) {
      // With no head yet there is nothing to place against, and the next `reposition` — from the
      // height row, or the next time this is pressed — picks it up.
      if (this.lastHead !== undefined) this.reposition(this.lastHead)
    }
    // Reaching stops mid-gesture, so anything held has to be let go rather than left down.
    this.rig.releaseAll()
  }

  supports(mode: ImmersiveMode): Promise<boolean> {
    return this.manager.supports(mode)
  }

  /**
   * Enter a session, to present `look` — by default the look the mode implies. A shell that has
   * passthrough asks for `immersive-ar` with `'vr'` to get the arcade room inside it (`setLook`).
   */
  enter(mode: ImmersiveMode, look: Look = mode === 'immersive-vr' ? 'vr' : 'mr'): Promise<boolean> {
    this.lookWanted = look
    return this.manager.enter(mode)
  }

  exit(): Promise<void> {
    return this.manager.exit()
  }

  /** Where the machine currently stands. The renderer applies this to the machine's group. */
  get placement(): Placement {
    return this.rig.placement
  }

  /**
   * One XR frame.
   *
   * Ordered deliberately: safety first (a recycle must not be delayed by a frame of input), then
   * hands, then placement, then controls. Placement before controls, because the rig converts
   * world positions into cabinet space and a machine that moved this frame moved before the hand
   * touched it.
   */
  advance(input: XRFrameInput, frameSeconds: number, out: PlayControls): XRFrameReport {
    this.toast = undefined
    this.elapsed += frameSeconds

    const action = this.safety?.advance(frameSeconds, input.visible) ?? { kind: 'none' as const }
    this.applySafety(action)

    this.hands.advance(input.frame, input.referenceSpace, input.inputSources, frameSeconds, input.pinch)
    this.drawHands(input)
    this.lastHead = input.head
    if(this.accountUtilitySuspended && input.head!==undefined){this.accountUtilityPending=false;this.accountUtilitySuspended=false}
    this.standOnTheRoomsFloor(input, frameSeconds)
    this.holdTheFloor(input)
    this.answerARecentre(input, frameSeconds)
    if (this.sayAgainAt !== undefined && this.elapsed >= this.sayAgainAt) {
      this.sayAgainAt = undefined
      this.sayPlacement('a second after the recentre', '[xr]')
    }
    this.sayWhatWasKept()
    // **The Builder's door lands at the bench** (M13, ADR 0015). The same entry the wrist row
    // makes, on the first frame that can make it; `enterBuild` says why a head is needed first.
    // The host is always asked — taking the word is what clears it — and the answer is dropped
    // when the mode is already on, which is what a recycle looks like from here.
    if (!this.buildAsked) {
      this.buildAsked = true
      const wanted = this.options.build?.wanted?.() ?? false
      this.buildWanted = wanted && this.buildOffered && !this.build.active
    }
    if (this.buildWanted && input.head !== undefined && !this.build.active && !this.accountUtilityPending) {
      this.buildWanted = false
      this.enterBuild()
    }
    // Leave build arms while the bench has edits nothing has kept (M13-15): undoable history,
    // and the draft on the bench is not the one the last Keep wrote.
    this.menu.unsavedBuild =
      this.build.active && (this.unfinishedBuildEdit() || this.build.canUndo && this.build.draft?.definition !== this.keptDefinition)
    this.menu.resettableBuild = this.build.active && this.build.canReset
    const menuWasOpen = this.menu.open
    const menuHadModalInput = (menuWasOpen && this.menu.presentation === 'normal') || this.machineGallery?.visible || this.galleryLoading
    this.advanceMenuPresentation(input, frameSeconds * 1000)
    // Presentation has one host owner. The shared menu still owns confirmation countdowns.
    this.menu.advance(undefined, false, frameSeconds * 1000)
    this.drawMenu()
    this.sayHands(input, this.menu.open !== menuWasOpen)
    this.advanceMenuStepRepeat(frameSeconds)
    if (this.accountUtilityPending || this.navigationRelease.blocked || menuHadModalInput || this.machineGallery?.visible || this.galleryLoading || (this.menu.open && this.menu.presentation === 'normal')) {
      if (this.suspendedWorldInput() && this.buildResumeHands === undefined) {
        const sides = [this.build.carry.heldBy, this.build.carry.secondHand, this.build.shaper.holding, this.build.inspector.holding,this.tools.heldBy]
        if (this.build.shaper.holdingBoth) sides.push('left', 'right')
        this.buildResumeHands = [...new Set(sides.filter((side): side is 'left' | 'right' => side !== undefined))]
        this.buildResumeReleased.clear(); this.buildResumeReady.clear()
      }
      this.rig.releaseAll()
      this.applyPlacement()
      if (this.build.active) this.poseTray()
      if (this.galleryLoading) this.toast = 'Preparing the arcade…'
      this.drawNotice(input, frameSeconds)
      return this.report(NO_CONFIRMATIONS)
    }
    if (!this.suspendedWorldInput()) this.buildResumeHands = undefined
    if (this.buildResumeHands !== undefined) {
      for (const side of this.buildResumeHands) {
        const held = side === 'left' ? this.hands.left : this.hands.right
        if (!held.present) continue
        if (!held.grab) this.buildResumeReleased.add(side)
        else if (this.buildResumeReleased.has(side)) this.buildResumeReady.add(side)
      }
      if (this.buildResumeHands.every(side => this.buildResumeReady.has(side))) this.buildResumeHands = undefined
      else {
        this.rig.releaseAll()
        this.toast = 'Your held edit is unchanged. Release, then pinch again to continue moving it.'
        this.drawNotice(input, frameSeconds)
        return this.report(NO_CONFIRMATIONS)
      }
    }

    // **One owner of what a hand is for** (M13-08). Transparent while Build mode is idle; while it
    // is on, the seated offer, the rig, the corner carry and the ritual's `startPlaying` run only
    // when this says so, and the tray and the carry run only when it says so.
    const arbiter = this.build.arbiter()

    if (input.head !== undefined && !this.comfortOn && arbiter.seatedOfferRuns) {
      const state = this.seated.advance(input.head.x, input.head.y, input.head.z, frameSeconds)
      if (state.offer) {
        this.toast = 'Playing seated? Open Settings / Comfort & seating. Hands or controllers.'
      }
    }

    this.advanceFloor(input)
    // **The placement ritual does not run while Build mode is on** (2026-09-13, the second device
    // day). It did: every frame it aimed the reticle at the room and, still `aiming` when the
    // author had come in through the Builder's door, placed the full-size machine at the reticle
    // on *any* pinch — which a relaxed hand makes constantly — into the slot Leave build returns
    // to. "Size and location of the pinball machine changes without request": on leaving, the
    // machine stood wherever the reticle last was, at the ritual's scale. Frozen here, the ritual
    // resumes exactly where it was when the mode ends: a placed machine returns to its place, and
    // an author who never placed one is asked to, as at the start of any session.
    if (!this.build.active) this.advanceRitual(input, frameSeconds)
    // After the ritual, so it draws what this frame decided — and here rather than inside
    // `advanceRitual`, whose early returns (a pending restore) would otherwise leave last
    // frame's reticle standing over a room the ritual has stopped describing. No reticle over a
    // bench: there is nothing to place, and a disc breathing on the desk beside the tray reads as
    // a control.
    this.reticleDrawn?.aimAt(this.build.active ? undefined : this.ritual?.view().reticle, this.elapsed)
    this.advanceTeleport(input)
    this.reconcileBay()
    this.lighting = lightingMatchFor(input.light)
    this.venue?.update(this.elapsed)
    this.advanceVenuePresence(input)
    // After every write to the placement this frame, so a reset restores where it ended up.
    this.rememberWhereItStands(input)

    // A hidden session gets no controls at all. A headset on a desk must not be pressing
    // anything, and a hand frozen mid-press would hold a flipper up until it came back.
    if (this.pausedByVisibility) {
      this.rig.releaseAll()
      // A part in the hand when the headset came off is a part nobody placed.
      if (this.build.active) this.dropTheCarry()
      return this.report(NO_CONFIRMATIONS)
    }

    if (this.build.active) this.advanceBenchPinch()
    if (arbiter.buildRuns) this.advanceBuild(input, frameSeconds)
    if (this.build.phase === 'testing') this.advanceTestTools()

    if (arbiter.rigRuns) {
      const options: { comfort?: boolean; bound?: BoundControls; carry?: boolean } = {
        comfort: this.comfortOn,
        carry: arbiter.cabinetGrabRuns,
      }
      if (input.bound !== undefined) options.bound = input.bound
      this.rig.advance(this.hands, frameSeconds, out, options)

      // XR-008's confirming toast, which was composed and dropped for four milestones. Taken
      // after the rig has advanced and only when nothing more urgent has already claimed the
      // line: a recycle countdown and a lost anchor both matter more than "controllers active".
      this.toast ??= this.rig.takeMethodToast()

      // Reaching for the machine rather than for a handle is what commits a placement (06 §3).
      if (this.rig.confirmations.length > 0 && arbiter.startPlayingRuns) this.ritual?.startPlaying()
    } else {
      // **Nothing stays in a hand the rig is not reading** (25 Sep 2026): Back to build with a
      // hand on the rod left it drawn over the bench until the next test, because only a frame
      // the rig runs can see the hand open. The headset's `CabinetIsAsleep` does the same.
      this.rig.releaseAll()
      // The rig is not reading the hands, but the scheme tracker still has to: a controller put
      // down mid-edit is 06 §2.5's "release an in-progress grab safely", and the grab in progress
      // is a part. The rig's own `onMethodChange` would do the same for the plunger.
      const change = this.rig.method.advance(this.hands.trackedSources(), frameSeconds * 1000)
      if (change !== undefined) {
        this.toast ??= change.toast
        if (change.cancelGrabs) this.dropTheCarry()
      }
    }

    this.drawControls()
    this.applyPlacement()
    // **Last, so it says whatever this frame decided** (M9-47). Every branch above may raise a
    // toast — the safety watchdog, the floor, the ritual, the venue, the seated offer, the input
    // scheme — and a panel drawn before them would be a frame behind on the one sentence a player
    // is being asked to act on.
    this.drawNotice(input, frameSeconds)
    // A rig that did not run this frame has last frame's confirmations still in its list, and a
    // click replayed seventy-two times a second is a buzz.
    return this.report(arbiter.rigRuns ? this.rig.confirmations : NO_CONFIRMATIONS)
  }

  /**
   * Forget the remembered placement.
   *
   * The wrist menu's Re-place does *not* call this — re-placing is a new position for the same
   * choices, and throwing the scale away every time would make the one control a player uses most
   * also the one that undoes their setup. This is for a player who wants a clean slate.
   */
  forgetPlacement(): void {
    // **Three things, and until M9-54 only two of them happened.** `forgetAnchor` removes this
    // product's record; `delete` releases the in-session object; and `releaseAnchor` gives the
    // *persistent* entry back to the runtime — the one a handle is a key into, and the one that
    // survived every forget this client has ever performed. Read before the record goes, because
    // the record is the only thing that knows the handle.
    void releaseAnchor(this.options.session?.() as XRAnchorSessionPort | undefined, rememberedHandle(this.storage))
    forgetAnchor(this.storage)
    this.anchor?.delete?.()
    this.anchor = undefined
    this.remembered = false
  }

  /** The venue, the panels and the machine's transform, torn down. */
  dispose(): void {
    this.cancelGalleryOpening()
    if (this.machineGallery !== undefined) {
      this.options.scene.remove(this.machineGallery.group)
      this.machineGallery.dispose(); this.machineGallery = undefined
    }
    if (this.build.active) this.leaveBuild()
    this.teardownVenue()
    const panel = this.panel
    if (panel !== undefined) {
      this.options.scene.remove(panel.group)
      panel.dispose()
      this.panel = undefined
    }
    const notice = this.notice
    if (notice !== undefined) {
      this.options.scene.remove(notice.group)
      notice.dispose()
      this.notice = undefined
    }
    const ghost = this.ghost
    if (ghost !== undefined) {
      this.options.scene.remove(ghost.group)
      ghost.dispose()
      this.ghost = undefined
    }
    this.dropTheMarks()
    this.manager.dispose()
  }

  /**
   * The player's own hands, drawn where the frame says they are (`ghostHands.ts`, 14 Sep 2026:
   * "in MR, user's hands disappear (hidden) when over left-palm and machine"). In the same
   * reference space as everything else, so the ghost sits on the passthrough hand.
   */
  private drawHands(input: XRFrameInput): void {
    this.joints.advance(input.frame, input.referenceSpace, input.inputSources)
    if (this.ghost === undefined) {
      if (!this.joints.left.present && !this.joints.right.present) return
      this.ghost = buildGhostHands()
      this.options.scene.add(this.ghost.group)
    }
    this.ghost.update(this.joints.left, this.joints.right)
    // **The pinch point, marked, where the room hides the real hand** (M13-28). In the arcade
    // room the author's only hand is the ghost, and where its thumb and index meet is a guess;
    // the carry is made with `HandReport.pinch`, so that is what is marked — brighter while
    // pinched. In passthrough the real hand is there and the dot would be noise on it.
    if (this.marks === undefined) {
      this.marks = buildPinchMarks()
      this.options.scene.add(this.marks.group)
    }
    this.marks.update(this.hands.left, this.hands.right, this.lookNow === 'vr')
  }

  // --- internals -------------------------------------------------------------------

  private onStart(mode: ImmersiveMode): void {
    this.elapsed = 0
    this.freshDrawings()
    // **The look, and the row that changes it** (15 Sep 2026, see `setLook`). In a passthrough
    // session the look is whatever the shell asked for and the row offers the other; an
    // `immersive-vr` session has one look and no row.
    this.lookNow = mode === 'immersive-ar' ? this.lookWanted : 'vr'
    this.switchTo = mode === 'immersive-ar' ? (this.lookNow === 'mr' ? 'vr' : 'mr') : undefined
    this.ritualPinchWas.left = true
    this.ritualPinchWas.right = true
    this.safety = new SessionSafety({
      ...(this.options.recycle === undefined ? {} : { recycle: this.options.recycle }),
      ...(this.options.recycleAfterSeconds === undefined ? {} : { afterSeconds: this.options.recycleAfterSeconds }),
      ...(this.options.recycleWarnSeconds === undefined ? {} : { warnSeconds: this.options.recycleWarnSeconds }),
    })
    this.rig.spring.settle()
    // **Both modes.** A broken floor is a fact about the headset, not about passthrough: in VR it
    // puts the venue's floor through the player's shins just as surely as it puts a cabinet above
    // their eyeline in MR. The one thing that is MR-only is *not moving the machine afterwards*,
    // because in MR the machine may be standing on a real table.
    this.floorSpace = new FloorAlignment({
      renderer: this.options.renderer as unknown as FloorRendererPort,
      storage: this.storage,
      offset: this.options.rigidTransform ?? defaultRigidTransform,
    })
    void this.refreshFloor()
    this.localSpace = undefined
    void this.requestLocalSpace()
    if (this.lookNow === 'mr') this.startMrLook()
    else this.startVrLook()
  }

  /**
   * The mixed-reality look: the room through the passthrough, the machine on its remembered spot
   * or in front of the player, the placement ritual and its reticle. Called at the start of an
   * `immersive-ar` session and when the wrist switches back to it (`setLook`).
   */
  private startMrLook(): void {
      // A fresh ritual every time, but not a fresh *choice*: the room may have been rearranged
      // since the last session and a remembered position that is now inside a sofa is worse than
      // one pinch — so the position is asked for again while the scale and the angle the player
      // chose are restored. Re-placing a remembered machine is one pinch; re-choosing tabletop at
      // 0.38 and turning it twenty degrees is a setup.
      this.ritual = new PlacementRitual({
        cabinetLength: this.options.dimensions.length,
        barHeight: this.options.dimensions.lockdownBarHeight,
      })
      this.surfaces = new SurfaceSource()
      // **The drawer `RitualView.reticle` waited four milestones for** (M9-53). Built with the
      // ritual and torn down with the session; every frame in between it draws exactly what the
      // view reports, so the comment below about "the player sees a reticle for a moment" is at
      // last a statement about the screen rather than about a field.
      this.reticleDrawn?.dispose()
      this.reticleDrawn = buildReticle()
      this.options.scene.add(this.reticleDrawn.group)
      this.remembered = false
      this.pending = undefined
      this.unlocatedFrames = 0
      this.anchor = undefined
      this.placeMachine(this.ritual.placement)
      this.teardownVenue()
      // Asking the runtime for yesterday's anchor takes a promise; putting the machine on it
      // takes a frame. The ritual runs in the meantime and is overtaken if the anchor arrives,
      // which is why the player sees a reticle for a moment even when the machine is about to
      // come back on its own — and why they are never left staring at nothing while a promise
      // that is going to be rejected settles.
      void this.beginRestore()
  }

  /**
   * The VR look: the arcade room, the machine at the player's bay, no ritual because the venue
   * decides where the machine stands. Called at the start of an `immersive-vr` session, at the
   * start of a passthrough session asked for as VR, and when the wrist switches to it.
   */
  private startVrLook(): void {
    this.ritual = undefined
    this.surfaces = undefined
    if (this.reticleDrawn !== undefined) {
      this.options.scene.remove(this.reticleDrawn.group)
      this.reticleDrawn.dispose()
      this.reticleDrawn = undefined
    }
    this.anchor = undefined
    this.pending = undefined
    this.buildVenue()
    // **Stood at the player once the head has settled** (15 Sep 2026), through the same wait a
    // wrist press gets: the first frames of a session report a head the runtime is still
    // deriving, and 14 Sep's first-frame stand put the room along a yaw nobody was facing —
    // "VR mode machine locations incorrect". Until then the bay stands at the origin.
    this.recentreWhy = 'start'
    this.recentreAskedAt = this.elapsed
    this.settleHead = undefined
  }

  /**
   * Fresh drawings for a fresh session (15 Sep 2026: "ghost hands disappeared in MR mode" — the
   * third session of one page, after a switch and an exit). The ghost and the control picture are
   * rebuilt lazily on the first frame that needs them; a few milliseconds once per session.
   */
  private freshDrawings(): void {
    const ghost = this.ghost
    if (ghost !== undefined) {
      this.options.scene.remove(ghost.group)
      ghost.dispose()
      this.ghost = undefined
    }
    this.dropTheMarks()
    this.picture = undefined
  }

  /** The pinch marks leave with the session that drew them, as the ghost does. */
  private dropTheMarks(): void {
    const marks = this.marks
    if (marks === undefined) return
    this.options.scene.remove(marks.group)
    marks.dispose()
    this.marks = undefined
  }

  private onEnd(mode: ImmersiveMode, deliberate: boolean): void {
    this.pendingDeparture = undefined
    // The flat client resets the root pose after this callback. Restore local support
    // geometry first so a seated VR fit cannot survive into desktop or a new MR session.
    fitCabinetToFloor(this.options.machine, undefined)
    this.cancelGalleryOpening()
    this.machineGallery?.hide()
    this.say(`[xr] ${mode} ended — ${deliberate ? 'the player asked' : 'ended by the runtime'}`)
    this.rig.releaseAll()
    if(this.accountUtilityPending)this.accountUtilitySuspended=true
    // **The draft survives a recycle; the carry does not, and neither does the bench's
    // coordinates** (standing rule 6, XR-030). The next session's tracking space may be
    // renumbered, so the bench is re-derived from the first head it reports rather than kept —
    // a world coordinate is not a place.
    if (this.build.active) {
      if(!this.accountUtilityPending)this.dropTheCarry()
      else {
        const owners=[this.build.carry.heldBy,this.build.carry.secondHand,this.build.shaper.holding,this.build.inspector.holding,this.tools.heldBy]
        if(this.build.shaper.holdingBoth)owners.push('left','right')
        this.buildResumeHands=[...new Set(owners.filter((x):x is 'left'|'right'=>x!==undefined))]
        this.buildResumeReleased.clear();this.buildResumeReady.clear()
      }
      this.benchStale = true
      this.buildReturn = undefined
    }
    this.teardownVenue()
    this.ritual = undefined
    if (this.reticleDrawn !== undefined) {
      this.options.scene.remove(this.reticleDrawn.group)
      this.reticleDrawn.dispose()
      this.reticleDrawn = undefined
    }
    this.surfaces = undefined
    // The flat screen must not keep the last pose of a hand that is no longer tracked.
    this.ghost?.hide()
    this.detachReset()
    this.floorSpace = undefined
    this.localSpace = undefined
    this.alignIn = 0
    this.safety = undefined
    this.pausedByVisibility = false
    this.buildWanted = false
    this.buildAsked = false
    this.palmSession.reset()
    this.navigationRelease.invalidateFrame()
    this.menuFeedback = undefined
    this.menu.close()
    this.menuPosePresentation = undefined
    this.controllerMenuWasHeld = false
    this.menuPauseRequested = false
    this.menuEngaged = false
    this.switchTo = undefined
    this.headRelation = undefined
    this.wornEye = undefined
    this.eyeBeforeReset = undefined
    this.options.onEnd?.(mode, deliberate)
  }

  private applySafety(action: SafetyAction): void {
    switch (action.kind) {
      case 'warn':
        this.toast = recycleWarning(action.seconds)
        return
      case 'recycle':
        // Only the session ends. The page, the game and the ball in play stay exactly where they
        // are — see `sessionSafety.ts`, and 06 §7 for why this exists at all.
        this.toast = 'Reconnecting the headset display — your game is still running.'
        void this.manager.exit()
        return
      case 'pause':
        this.say('[xr] visibility: hidden — the runtime has taken the session away; frames stop here')
        this.pausedByVisibility = true
        this.rig.releaseAll()
        return
      case 'resume':
        this.say('[xr] visibility: visible again')
        this.pausedByVisibility = false
        return
      default:
        return
    }
  }

  private async beginRestore(): Promise<void> {
    const session = this.options.session?.()
    if (session === undefined) return
    const outcome = await restoreAnchor(this.storage, session as XRAnchorSessionPort)
    // The session may have ended while the promise settled.
    if (!this.manager.presenting) return
    this.pending = outcome
    if (outcome.kind === 'lost') {
      // The choices survive; the position does not (06 §3). One pinch, with the scale already
      // right, rather than a whole setup.
      this.ritual?.setScaleMode(outcome.mode, outcome.scale)
      this.toast = 'This room has moved since last time — place the machine again.'
      this.pending = undefined
    }
  }

  /**
   * Put the machine back on a restored anchor, once a frame can say where the anchor is.
   *
   * Gives up after a second of trying. An anchor the runtime restored but cannot locate is an
   * anchor whose part of the map is not currently tracked, and waiting indefinitely leaves the
   * player looking at a reticle they cannot use because the ritual has been overtaken by a
   * machine that never arrives.
   */
  private locateRestored(input: XRFrameInput): void {
    const pending = this.pending
    if (pending === undefined || pending.kind !== 'anchor') return
    const pose = input.frame.getPose(pending.anchor.anchorSpace, input.referenceSpace)
    // **A head is as necessary as a pose, because the judgement below needs one.** Waiting is the
    // point rather than a limitation — the Quest client splits `Restore` from `Judge` for this
    // exact reason, and states it: *"a placement judged against a head nobody has seen yet would
    // be judged against zero, and every anchor in the world sits above that."*
    //
    // A frame with no viewer pose is counted against the same second-long budget as a frame with no
    // anchor pose, and the two share it accurately rather than merely conveniently: `head` is
    // undefined exactly when `getViewerPose` returns nothing, which is when tracking is not
    // established — and an anchor cannot be located in that state either. **They fail together**,
    // and the recovery is the same sentence to the player.
    const head = input.head
    if (pose === undefined || pose === null || head === undefined) {
      this.unlocatedFrames++
      if (this.unlocatedFrames > 72) {
        this.pending = undefined
        this.ritual?.anchorLost()
        this.ritual?.setScaleMode(pending.record.mode, pending.record.scale)
        this.toast = 'This room has moved since last time — place the machine again.'
      }
      return
    }
    const placement = placementFromAnchor(pending.record, pose.transform.position, pose.transform.orientation)
    // **A saved placement is only as good as the floor it was saved against, and a floor moves
    // both ways.** Re-measured lower, yesterday's cabinet is in the ceiling; re-measured higher, it
    // is in the ground. `doubtsAboutRestore` asks both, in the order `MixedRealityRig.Judge` asks
    // them, because until now this asked only the second: the ground case landed on 25 Aug and the
    // ceiling case — the one `barIsPlayable` was written for, and the one the headset actually
    // reported on 22 Aug — was never wired in on this side at all.
    //
    // **Judged against this player's settled eye height where there is one**, not against a single
    // frame of it, for the reason the Quest client gives: this runs once per session and the
    // refusal is one-way, so one bad sample either keeps an unplayable anchor or throws away a
    // good one for good. With no samples the frame beats the nominal figure, because a measurement
    // beats a guess.
    const eyeHeight =
      loadEyeHeights(this.storage).length > 0 ? knownEyeHeight(this.storage) : head.y
    const doubt = doubtsAboutRestore(
      placement.y,
      placement.y + this.options.dimensions.lockdownBarHeight * placement.scale,
      eyeHeight,
    )
    if (doubt !== undefined) {
      this.pending = undefined
      this.ritual?.anchorLost()
      this.ritual?.setScaleMode(pending.record.mode, pending.record.scale)
      // **The record is kept**, exactly as it is when an anchor never localises: it carries the
      // scale and the mode the player chose, and those are still theirs. Only the *position* was
      // measured against a floor that has since moved.
      //
      // No anchor is let go of here, and the Quest client's *"a rule that only guards the front
      // door guards nothing"* does not apply: `this.anchor` is the only thing `advanceRitual`
      // consults, it is assigned on the accepting path alone, and this path leaves it unset — so
      // there is nothing still tracking the refused placement to re-apply it next frame.
      //
      // Same sentence as an anchor that never localises, because it is the same sentence to them.
      this.toast = 'This room has moved since last time — place the machine again.'
      return
    }
    this.pending = undefined
    this.anchor = pending.anchor
    this.remembered = true
    const ritual = this.ritual
    if (ritual !== undefined) {
      ritual.setScaleMode(pending.record.mode, pending.record.scale)
      // **Adopted, not recentred.** `recenter` did nothing while the ritual was scanning and left
      // it holding a placement the machine was not at; a nudge from the place page then applied
      // that placement. The ritual now holds the one the anchor gave, as placed.
      ritual.adopt(placement)
    }
    this.placeMachine(placement)
    // Said, because until 14 Sep 2026 this was the one move of the machine no line announced,
    // and a machine restored with yesterday's yaw in today's space looked like one that had
    // turned to face a wall on its own.
    this.sayPlacement(
      `restored from the anchor (yaw ${pose.transform.orientation === undefined ? 'from the record' : "from the anchor's own pose"})`,
      '[xr]',
    )
    // **A restored anchor is honoured even when it is out of reach, and the player is told.**
    // The anchor is the authority on *where*: it is a handle into the headset's own map, and the
    // machine it describes is standing on a real table in a real room. Pulling it toward whoever
    // happens to be standing here would drag it off that table — which is why `place()` clamps a
    // hit test and this does not. But a placement the player cannot touch, restored in silence,
    // reads as the machine having gone missing, and the recovery for it is a wrist-menu item they
    // have no reason to look for. So it is said out loud instead of acted on.
    // The same `head` the judgement above was made against — this point is only reached once one
    // has arrived, so there is no longer anything to check for.
    if (!withinReach(placement, head.x, head.z, this.options.dimensions.length)) {
      this.toast = 'Your machine is where you left it, across the room. '
        + 'Walk over, or use Recenter to bring it to you.'
    }
  }

  /**
   * Look at another bay and pinch (M5-23). VR only.
   *
   * Latched on the pinch edge, and refused while the wrist menu is open or while a hand is
   * touching the machine: a player mid-plunge has both hands pinched, and teleporting them out of
   * their own bay for it would be the worst bug in the venue.
   */
  private advanceTeleport(input: XRFrameInput): void {
    // A pinch while building is a part grab, and a bay is a different machine.
    if (this.venue === undefined || input.head === undefined || this.build.active) {
      this.pinchWasDown = false
      return
    }
    const pinching =
      (this.hands.left.grab || this.hands.right.grab) && !this.menu.open && !this.rig.holdingMachine
    const edge = pinching && !this.pinchWasDown
    this.pinchWasDown = pinching
    if (!edge) return
    this.teleport(input.head)
  }

  /**
   * What each bay's wrist-menu row says (M8-20).
   *
   * Empty outside a venue: mixed reality is one cabinet in somebody's living room, and there is no
   * row of machines to go to.
   */
  private bayLabels(): readonly string[] {
    if (this.venue === undefined || this.venuePresence === undefined) return []
    return bayStatuses(this.floor.bays.length, this.floor.index, this.occupants()).map(bayRowLabel)
  }

  /**
   * What the rest of the row sounds like from where this player is standing (M8-21).
   *
   * Empty outside a venue, which is mixed reality: one cabinet in a living room, and no
   * neighbours to hear. The machine runtime asks once a frame and hands it to the ambience.
   */
  get roomSounds(): readonly BaySound[] {
    if (this.venue === undefined) return []
    // **Who is where fits in six bits**, so the answer is recomputed when the room changes rather
    // than sixty times a second to produce the same five objects. Asked every frame, and the
    // frame's cost is a loop over the roster and one comparison — the array behind it survives
    // until somebody moves.
    const port = this.venuePresence
    let mask = 0
    if (port !== undefined) {
      for (const row of port.others()) {
        const bay = port.bayOf(row.participantId)
        if (typeof bay === 'number' && bay >= 0 && bay < 30) mask |= 1 << bay
      }
    }
    const key = mask * 64 + this.floor.index
    if (key !== this.roomKey || this.room === undefined) {
      this.roomKey = key
      const occupied = new Set<number>()
      for (const bay of this.floor.bays) {
        if ((mask & (1 << bay.index)) !== 0) occupied.add(bay.index)
      }
      this.room = baySounds(this.floor.bays, this.floor.index, occupied)
    }
    return this.room
  }

  /** Who is standing where, from the roster. Empty with no port — an arcade with nobody in it. */
  private occupants(): readonly BayOccupant[] {
    const port = this.venuePresence
    if (port === undefined) return []
    return port.others().map((row) => ({
      displayName: row.handle,
      bay: port.bayOf(row.participantId),
    }))
  }

  /**
   * Take the room's answer about where this player is standing (M8-20).
   *
   * **A bay holds one player and the roster decides which** — the tiebreak is the participant id,
   * not who asked first, so two players who pinch the same free bay in the same moment are
   * separated afterwards and one of them is somewhere they did not choose. Cheap to check: one
   * comparison a frame against a number the port already holds.
   *
   * Told once per move rather than every frame, because the toast is a fact about a change and a
   * line that reprinted itself at 72 Hz would never leave the screen.
   */
  private reconcileBay(): void {
    const settled = this.venuePresence?.myBay?.()
    if (settled === undefined || this.venue === undefined) return
    if (settled === null) {
      // Present, standing at no machine: the seventh player in a six-bay room (M8-01).
      if (!this.toldArcadeFull) {
        this.toldArcadeFull = true
        this.toast = ARCADE_FULL_LINE
      }
      return
    }
    this.toldArcadeFull = false
    if (settled === this.askedBay) this.askedBay = undefined
    const verdict = roomVerdict(settled, this.floor.index, this.askedBay, this.elapsed - this.askedAt)
    if (verdict !== 'move') return
    this.askedBay = undefined
    this.floor.goTo(settled)
    this.standAtBay(this.lastStandingHead())
    this.venue.setPlayerBay(this.floor.index)
    this.toast = movedByRoomLine(settled)
  }

  /**
   * Re-read the runtime's floor and arrange to measure the player against it.
   *
   * Called on entry and on every platform recentre. **Asking again is the entire point**: the
   * runtime goes on refining its floor estimate after the session has started and does not
   * reliably announce when it does, so a space captured at entry can be half a metre out with no
   * symptom a plausibility check could detect — it reports an ordinary standing height, just the
   * wrong one.
   */
  /**
   * Measure the player against the space that was just installed, once it has taken effect.
   *
   * A believable height teaches the memory who this player is; an impossible one is corrected
   * against what the memory already knows. Both happen here rather than in `refreshFloor` because
   * both need a *frame*, and the space is installed from a promise.
   */
  private advanceFloor(input: XRFrameInput): void {
    if (this.alignIn === 0 || input.head === undefined) return
    this.alignIn -= 1
    if (this.alignIn > 0) return
    const outcome = this.floorSpace?.alignToViewer(input.head.y)
    if (outcome?.correction === undefined) return
    // Said plainly, because the app has just papered over a device fault and the player is the
    // only one who can actually repair it.
    // **The break is the author's, and the sentence is shorter than it was** (M9-47). At 83
    // characters this wrapped to a second row of 275 dots against the panel's 256 and was silently
    // clipped — which is the failure `SessionNotice`'s own header warns about: *"fits by
    // construction" has an unstated author, and a new caller replaces them.* The unstated author
    // here was the DOM, which wraps anything.
    this.toast = 'Floor height was corrected - re-run Space Setup on your headset.'
  }

  /**
   * The origin that does not move (`floorAlignment.ts`, fault 4). Asked for once per session; a
   * runtime without `local` keeps the floor it has.
   */
  private async requestLocalSpace(): Promise<void> {
    const session = this.options.session?.() as FloorSessionPort | undefined
    if (session?.requestReferenceSpace === undefined) return
    const floor = this.floorSpace
    try {
      const space = await session.requestReferenceSpace('local')
      if (this.floorSpace === floor) this.localSpace = space
    } catch {
      // The session ended, or the runtime has no `local`: nothing to hold against.
    }
  }

  /**
   * Hold the floor where the session started when the runtime moves it silently (14 Sep 2026,
   * the 23:11 VR session: headset off and on, no `reset`, a standing player reading 1.20 m and
   * the playfield at their eyes). `FloorAlignment.holdAgainstLocal` says how.
   */
  private holdTheFloor(input: XRFrameInput): void {
    const hold = this.floorSpace?.holdAgainstLocal(input.frame, this.localSpace)
    if (hold === undefined) return
    if (hold.kind === 'baseline') {
      this.say(`[xr] floor: the local origin reads ${hold.localHeight.toFixed(3)} m above the runtime's floor — held from here`)
      return
    }
    this.say(
      `[xr] floor: the runtime moved its floor ${Math.abs(hold.moved).toFixed(3)} m ${hold.moved > 0 ? 'up' : 'down'} under the player with no reset`
        + ` (local origin now ${hold.localHeight.toFixed(3)} m above it) — put back where the session started`,
    )
    this.toast = 'Floor moved under you — put back.'
  }

  private async refreshFloor(): Promise<void> {
    const floor = this.floorSpace
    if (floor === undefined) return
    const adopted = await floor.adoptRuntimeFloor(this.manager.current as FloorSessionPort | undefined)
    // The session can end while the promise settles; `floor` is cleared by `onEnd` and this is
    // then talking about a session nobody is in.
    if (this.floorSpace !== floor) return
    if (adopted) this.attachReset()
    this.alignIn = 2
  }

  /**
   * Listen for the runtime moving its own origin.
   *
   * A platform recentre fires `reset` on the reference space. Nothing in this client listened for
   * it, so a long-press moved the *player* and left the machine at coordinates that now mean a
   * different physical spot — commonly behind them. That is the same complaint as "the machine is
   * far away", arrived at from the other direction.
   */
  private attachReset(): void {
    const space = this.options.renderer as unknown as {
      xr?: { getReferenceSpace?: () => unknown }
    }
    const current = space.xr?.getReferenceSpace?.() as
      | { addEventListener?: (t: string, l: () => void) => void; removeEventListener?: (t: string, l: () => void) => void }
      | undefined
    if (current === this.resetSpace) return
    this.detachReset()
    if (current?.addEventListener === undefined) return
    current.addEventListener('reset', this.onReferenceSpaceReset)
    this.resetSpace = current
  }

  private detachReset(): void {
    this.resetSpace?.removeEventListener?.('reset', this.onReferenceSpaceReset)
    this.resetSpace = undefined
  }

  /**
   * The runtime re-centred. Bring the machine back to the player and re-read the floor.
   *
   * **Bound rather than a method**, because it is handed to `addEventListener` and has to be the
   * same function object at removal — an arrow field is the only form that guarantees that.
   *
   * A recentre cannot move the floor, so the height half of the problem needs `refreshFloor`, and
   * the position half needs the machine moved: every placed thing keeps its coordinates while the
   * player's own pose changes underneath it.
   */
  private readonly onReferenceSpaceReset = (): void => {
    this.navigationRelease.invalidateFrame()
    this.palmSession.reset(); this.menuFeedback = undefined
    this.menu.closeQuick(); this.panel?.release(); this.menuPinchWasDown = true
    this.menuPosePresentation = undefined; this.menuContactContext = undefined
    this.suppressQuickUntilLower = true; this.menuNeedsReposition = true
    void this.refreshFloor()
    // Both modes (14 Sep 2026). In VR the venue is stood at the head, so a new origin is a new
    // head and the venue is re-stood at it once the head settles, exactly as the machine is in
    // MR — where it may be standing on a real table and the player has just been told where it
    // is by their own eyes.
    if (this.ritual === undefined && this.venue === undefined) return
    // **Answered on the next frame, not here** (13 Sep 2026). The event arrives between frames,
    // and the head the ritual last observed is in the space that has just stopped existing: a
    // recentre answered from it stood the machine in front of where the player *had been*, read
    // in coordinates that now mean somewhere else — "Meta recenter does not work correctly". And
    // it wrote the ritual's full-size placement straight onto the rig, Build mode or not, which
    // is how "in build mode, movement commands change size of machine" happened: a long-press
    // while building turned the bench into a full cabinet. `answerARecentre` waits for a head in
    // the new space and knows which mode it is in.
    this.recentreWhy = 'reset'
    this.recentreAskedAt = this.elapsed
    this.resetsPending += 1
    this.settleHead = undefined
    // The eye height the headset was last worn at, before any of this burst — the second and
    // third reset of a long-press find `wornEye` unchanged, because it is not read while a
    // recentre is pending.
    if (this.eyeBeforeReset === undefined) this.eyeBeforeReset = this.wornEye
    // **Whose reset is it** (15 Sep 2026, the 01:37 round). With tracked hands the Quest recentres
    // on a pinch held for two seconds — that is Meta's recentre gesture, and it is also what a
    // hand does holding the plunger. The one thing that tells them apart is where the hand is: a
    // reset that arrives with a hand on the rod or over a control is the plunger's, and the
    // machine keeps its place; a reset with the hands away from the machine — or off, the headset
    // going back on — is the player's, and the machine comes to them (the owner's rule: putting
    // the headset on and Meta's Recenter set the machine's place). Any reset of a burst at the
    // machine makes the burst the machine's.
    const at = this.handsAtTheMachine()
    if (at) this.resetAtTheMachine = true
    this.say(
      `[xr] reference space reset — ${at ? 'a hand is at the machine: it keeps its place' : 'no hand at the machine: the machine comes to the head'} once the head has settled (${this.resetsPending} pending)`,
    )
  }

  /** Is a hand at the machine — on the rod or over a control — right now? See `onReferenceSpaceReset`. */
  private handsAtTheMachine(): boolean {
    if (this.rig.plungerHeld || this.rig.plungerOver) return true
    for (const hand of [this.hands.left, this.hands.right]) {
      if (hand.present && hand.kind === 'hands' && this.handAtAControl(hand)) return true
    }
    return false
  }

  /**
   * Bring it to you, pressed on the wrist: answered the same deferred way a `reset` is.
   *
   * **The press is made looking at the wrist** (the 00:05 session, 14 Sep 2026): a recentre from
   * the head this frame has stands the machine toward the left hand — 40–50° from where the
   * player is facing — and the row read as broken. So the row asks, and `answerARecentre` stands
   * the machine once the head has come up level and held still, which is what a person does
   * after pressing it: look where they want the machine to be.
   */
  private askARecentre(): void {
    if (this.recentreWhy === undefined) this.recentreWhy = 'menu'
    this.recentreAskedAt = this.elapsed
    this.settleHead = undefined
    this.toast = LOOK_WHERE_YOU_WANT_IT
  }

  /**
   * Why a recentre is waiting — a `reset`, a wrist press, or the start of a VR session — and
   * `undefined` when none is.
   */
  private recentreWhy: 'reset' | 'menu' | 'start' | undefined
  /** When it was asked for, in `elapsed` seconds; the settle clock starts from it. */
  private recentreAskedAt = 0
  /** Resets since the last answer: one long-press arrives as two or three. */
  private resetsPending = 0
  /** The head last seen while waiting, for the stillness test. */
  private settleHead: { x: number; y: number; z: number } | undefined

  /**
   * The frame after the runtime re-centred: stand the machine in front of the player again, in
   * the new space, in the mode the player is in (XR-002c: Meta's Recenter is one of the moves the
   * player makes on purpose).
   *
   * Building: the bench is re-derived from this head (`benchStale`, the recycle's own path) and so
   * is the placement Leave build returns to — both were coordinates in the old space. Otherwise the
   * ritual is told this head first and recentres from it; the anchor goes, and the placement is
   * written down again once the player reaches for the machine (`remembered`).
   */
  /** Said a second after a recentre lands, so a machine that drifted after the answer is caught. */
  private sayAgainAt: number | undefined

  private readonly floorWatch = new FloorPlaneWatch()

  /**
   * Move the presented space onto the room scan's floor when the two disagree (`floorPlane.ts`,
   * 14 Sep 2026). The 19:19 session: after a Meta recentre the runtime's floor sat 0.42 m above
   * the room's, a standing player read 1.20 m — a believable, seated height, so `advanceFloor`
   * had nothing to correct — and the cabinet stood on the invented floor with its bar at the
   * player's chest. The scan's `floor` plane is the ground; its height here is the error.
   */
  private standOnTheRoomsFloor(input: XRFrameInput, frameSeconds: number): void {
    const move = this.floorWatch.advance(input.floorY, frameSeconds)
    if (move === undefined) return
    if (this.floorSpace?.alignToFloorPlane(move) !== true) return
    this.say(
      `[xr] floor: the room's floor plane read ${move.toFixed(3)} m ${move > 0 ? 'above' : 'below'} the presented floor — the world moved onto it`,
    )
    if (Math.abs(move) >= FLOOR_SAID_FROM) this.toast = FLOOR_FROM_THE_ROOM
  }

  /**
   * The palm number, or "palm down" for a palm that is not a menu (`palmReach.ts`, 14 Sep 2026):
   * a fist, a hand too far from the face, or a hand at one of the cabinet's controls. The 18:58
   * session opened the menu on a hand reaching down to the credit button; the 19:52 session held
   * it open on a relaxed fist for thirty seconds of building, and opened it five times on a hand
   * reaching for the start button. A controller has no palm number; a hand nobody can place is
   * left to the number alone. `whyNotAMenu` is kept for the hands line.
   */
  private advanceMenuPresentation(input: XRFrameInput, frameMs: number): void {
    const left = this.hands.left, head = input.head
    const hand = left.present && left.kind === 'hands'
    const right = this.hands.right, waiting = this.navigationRelease.blocked
    const freshOutward=head===undefined?undefined:this.navigationNormal.set(head.x-right.tip.x,head.y-right.tip.y,head.z-right.tip.z).normalize()
    this.navigationRelease.advance(right.present && input.visible && head!==undefined,
      right.kind === 'controllers' ? right.trigger : right.grab && right.grabSource === 'runtime', right.tip,freshOutward)
    if (waiting && !this.navigationRelease.blocked) this.say('[xr] menu: source-pointer released; destination input ready')
    this.menuTrackingVisible = input.visible
    if (this.menuNeedsReposition && head !== undefined) {
      this.panel?.release(); this.menuContactContext=undefined; this.menuPinchWasDown=true
      this.machineGallery?.cancelContact()
      this.menuStand=undefined;this.positionMenuGallery(); this.menuNeedsReposition = false
    }
    if(this.accountUtilityPending)return
    const forward = head?.forward ?? { x: -Math.sin(head?.yaw ?? 0), y: 0, z: -Math.cos(head?.yaw ?? 0) }
    this.palmSession.advance({
      tracked: hand && input.visible, headKnown: head !== undefined,
      head: head ?? { x: 0, y: 0, z: 0 }, palm: left.palm,
      normal: input.palmNormal ?? { x: 0, y: 0, z: 0 }, headForward: forward,
      openHand: palmIsOpen(left), atControl: hand && this.handAtAControl(left),
    }, frameMs)
    this.whyNotAMenu = hand && !this.palmSession.isOpen ? 'raise an open palm toward your face' : undefined
    if (this.gamePaused || this.options.build?.tools?.frozen() === true) this.menuPauseRequested = false
    if (this.palmSession.lostEdge) {
      this.menuFeedback = undefined
      this.panel?.release(); this.menuPinchWasDown = true
    }
    if (input.visible && head !== undefined && hand && left.palm.y < head.y - .42) this.suppressQuickUntilLower = false
    // Or turned away (the owner, 24 Oct 2026, on the native 1.0.212: "left-palm menu not always
    // appearing"): at the bench the hands never drop 0.42 m below the eyes, and a tracked palm
    // turned away has ended that presentation. The session starts again, so the palm must be
    // raised and held — the dwell — before the quick menu opens.
    else if (this.suppressQuickUntilLower && input.visible && head !== undefined && hand && input.palmNormal !== undefined) {
      const facing = palmFacing(input.palmNormal, left.palm, head)
      if (facing !== undefined && facing < PALM_UP_EXIT) { this.suppressQuickUntilLower = false; this.palmSession.reset() }
    }
    const modal = this.machineGallery?.visible || this.galleryLoading || (this.menu.open && this.menu.presentation === 'normal')
    if (modal) this.suppressQuickUntilLower = true
    const controllerHeld = input.visible && input.gripHeld && !this.rig.holdingMachine &&
      (this.hands.left.kind === 'controllers' || this.hands.right.kind === 'controllers')
    if (controllerHeld && !this.controllerMenuWasHeld && !this.machineGallery?.visible && !this.galleryLoading) {
      if (this.menu.open && this.menu.presentation === 'normal') this.closeNormalMenu()
      else this.openFullMenu()
    }
    this.controllerMenuWasHeld = controllerHeld
    if (this.palmSession.loweredEdge) {
      this.menu.closeQuick(); this.panel?.release(); this.menuPinchWasDown = true
      this.menuFeedback = undefined
    } else if (!this.palmSession.isOpen) this.menu.closeQuick()
    else if (!modal && !this.suppressQuickUntilLower) {
      const wasOpen = this.menu.open
      if (this.menu.openQuick() && !wasOpen) {
        this.menuPosePresentation = undefined; this.menuContactContext = undefined
        this.panel?.release(); this.menuPinchWasDown = true
      }
    }
  }

  private whyNotAMenu: string | undefined
  private readonly cabinetScratch: MutablePoint = { x: 0, y: 0, z: 0 }

  /** Is the hand's tip or palm at one of the cabinet's controls, in the cabinet's own frame? */
  private handAtAControl(hand: HandReport): boolean {
    const placement = this.rig.placement
    const tip = toCabinetPoint(placement, hand.tip.x, hand.tip.y, hand.tip.z, this.cabinetScratch)
    if (onAControl(this.rig.controls, tip.x, tip.y, tip.z)) return true
    const palm = toCabinetPoint(placement, hand.palm.x, hand.palm.y, hand.palm.z, this.cabinetScratch)
    return onAControl(this.rig.controls, palm.x, palm.y, palm.z)
  }

  private answerARecentre(input: XRFrameInput, frameSeconds: number): void {
    const why = this.recentreWhy
    if (why === undefined) return
    const head = input.head
    if (head === undefined) return
    // **A frame that took longer than a frame restarts the clock** (14 Sep 2026). The 18:58
    // session had no frames for 67 s — the headset off — and the first frame back carried a
    // `reset` and a `frameSeconds` of 7.95 s, which read as "waited 7.95 s" and answered from a
    // head the runtime was still re-deriving, at 0.80 m, with the bar above the eyes.
    if (frameSeconds > RECENTRE_STALL_S) {
      this.recentreAskedAt = this.elapsed
      this.settleHead = undefined
      return
    }
    const ritual = this.ritual
    // Building, the bench is re-stood from the head with or without a ritual; playing, the
    // ritual (MR) or the venue (VR, 14 Sep 2026) is what knows where the machine goes.
    if (ritual === undefined && this.venue === undefined && !this.build.active) return
    // **Once the head has settled, not on the first frame after the event** (13 Sep 2026, the
    // third device round). One long-press arrived as three `reset`s in 1.5 s, and the first
    // frames after each reported the head at 0.81 m, then 1.77 m, then 1.30 m — the runtime
    // re-deriving its floor — so the machine was re-stood three times, once with its lockdown bar
    // ten centimetres *above* the eyes. 06 §3.2: *"a re-placement is a follow if it happens often
    // enough, so anything that can trigger one needs a rate a person would recognise as
    // deliberate — which is what `SettleSeconds` is for."* The same three quarters of a second
    // the native client waits, a believable eye height, and a head that has stopped moving; a
    // floor the runtime never makes believable is answered anyway after `RECENTRE_SETTLE_MAX_S`.
    const waited = this.elapsed - this.recentreAskedAt
    const still =
      this.settleHead !== undefined
      && Math.abs(head.y - this.settleHead.y) < RECENTRE_STILL_Y
      && Math.hypot(head.x - this.settleHead.x, head.z - this.settleHead.z) < RECENTRE_STILL_XZ
    this.settleHead = { x: head.x, y: head.y, z: head.z }
    const forward = head.forward ?? forwardFromYaw(head.yaw, head.pitch ?? 0)
    // **Not still looking at the wrist the row was pressed on** — and nothing about level. The
    // first rule here was "within twenty degrees of the horizon", and it held both of the 18:58
    // session's answers to the three-second cap: a pinball player looks *down*, at the playfield
    // and at the floor the machine is about to stand on. What the rule meant to exclude is a
    // head still turned to the palm, and `lookingAt` says that and only that.
    const left = this.hands.left
    const atTheWrist =
      left.present && left.kind === 'hands' && palmWithinReach(head, left.palm) && lookingAt(head, forward, left.palm)
    const believable = believableFloor(head.y)
    const settled = waited >= RECENTRE_SETTLE_S && still && believable && !atTheWrist
    if (!settled && waited < RECENTRE_SETTLE_MAX_S) return
    this.recentreWhy = undefined
    const resets = this.resetsPending
    this.resetsPending = 0
    const cause =
      why === 'reset' ? `after ${resets} reset${resets === 1 ? '' : 's'}` : why === 'start' ? 'at the start' : 'after the wrist press'
    const pitchDeg = ((Math.asin(Math.max(-1, Math.min(1, forward.y))) * 180) / Math.PI).toFixed(0)
    const reasons = [
      ...(still ? [] : ['moving']),
      ...(believable ? [] : ['floor unbelievable']),
      ...(atTheWrist ? ['looking at the palm'] : []),
    ]
    const state = settled ? 'settled' : `not settled (${reasons.join(', ') || 'no still frame yet'}) — answered anyway`
    this.say(
      `[xr] recentre answered ${cause}, ${waited.toFixed(2)} s, head ${state} at ${head.y.toFixed(2)} m, ${pitchDeg}° ${Number(pitchDeg) < 0 ? 'down' : 'up'}, at (${head.x.toFixed(2)}, ${head.z.toFixed(2)})`,
    )
    this.sayAgainAt = this.elapsed + 1
    // **A reset the machine fired moves nothing** (15 Sep 2026, the owner's rule: "reset should
    // do nothing. the only thing that should set the location of the machine is the putting on
    // the headset, the movement commands through the relevant left-palm buttons and the Meta
    // Recenter command"). The evening's tap had the Quest firing one every time a pinch was
    // held on the plunger, and each re-stood the machine: "moves without prompting". Such a
    // reset keeps the machine where it physically stood. A reset made with the hands away from
    // the machine is Meta's Recenter — or the headset going back on — and brings the machine to
    // the head, as 13 Sep decided (01:37: "using Meta recenter briefly works, but machine moves
    // back to old/wrong place" was every reset being kept).
    if (why === 'reset') {
      const atTheMachine = this.resetAtTheMachine
      this.resetAtTheMachine = false
      if (atTheMachine) {
        this.keepAcrossReset(head)
        return
      }
      this.floorAcrossReset(head.y)
    }
    // Asked for — the wrist row, the player's recentre, or the first settled head of a VR
    // session: the machine comes to this head.
    if (ritual !== undefined) {
      ritual.observe(head.x, head.y, head.z, forward.x, forward.y, forward.z)
      this.anchor = undefined
      this.remembered = false
      ritual.recenter(this.comfortOn && !this.build.active ? this.seatedRitualStand(ritual, head) : undefined)
    }
    if (this.build.active) {
      if (ritual !== undefined) this.buildReturn = ritual.placement
      // A VR bench: the room comes to the head too, and so does the placement Leave build
      // returns to (`standAtBay` writes it through `placeMachine`).
      else if (this.venue !== undefined) this.standAtBay(standingHead(head))
      this.benchStale = true
      if (why === 'menu') this.toast = 'Brought the bench to you.'
      else if (why === 'reset') this.toast = 'View recentred — the bench came back to you.'
      return
    }
    if (ritual === undefined) {
      // VR (14 Sep 2026): the venue is stood at this head.
      if (this.venue === undefined) return
      this.standAtBay(standingHead(head))
      if (why === 'start') {
        this.sayStood()
        return
      }
      this.toast = why === 'reset' ? 'View recentred — the machine came back to you.' : 'Brought it to you.'
      this.sayPlacement('recentred', '[xr]')
      return
    }
    this.rig.placement = ritual.placement
    this.toast = why === 'reset' ? 'View recentred — the machine came back to you.' : 'Brought it to you.'
    this.sayPlacement('recentred', '[xr]')
  }

  /**
   * Keep the machine where it physically stood across a reset (15 Sep 2026). See `answerARecentre`.
   *
   * The runtime renumbered its space; the player did not move. In MR a followed anchor is
   * already the same point in the room, read in the new space (06 §3.1's fourth rule) — it is
   * kept, not dropped. Without one, and in VR, the machine is put back at the relation to the
   * head it had on the last frame before the burst (`headRelation`), so a player who had stepped
   * back from the machine is still stepped back from it. The bench is head-relative by
   * construction and is re-derived; the placement Leave build returns to keeps its relation. The
   * floor is the one thing that is corrected, because a runtime that re-read it wrongly has moved
   * the machine's height under a player who did not move — `floorAcrossReset`.
   */
  private keepAcrossReset(head: NonNullable<XRFrameInput['head']>): void {
    this.floorAcrossReset(head.y)
    const kept = this.placementFromRelation(head)
    if (this.build.active) {
      if (kept !== undefined) this.buildReturn = kept
      // **Test play: the reset was the plunger's pinch-hold** (25 Sep 2026, the build-85 round).
      // Re-deriving the full-size test placement from a head leaning in to pull moved the machine
      // under the held rod, and it read as a plunger stuck at the end of its travel.
      if (this.build.phase === 'testing' && this.testRelation !== undefined) {
        const at = headPoseOf(head)
        const rel = this.testRelation
        const p = toVenue(rel.at, at)
        this.testKept = { x: p.x, y: p.y, z: p.z, yaw: yawToVenue(rel.yaw, at), scale: rel.scale }
        this.say('[xr] reset at the machine during Test play: the machine under test keeps its place')
      }
      this.benchStale = true
      this.say('[xr] reset: the bench is re-derived from this head; the placement Leave build returns to keeps its place')
      return
    }
    if (this.ritual !== undefined && this.anchor !== undefined) {
      this.say('[xr] reset: the machine keeps its place — the anchor is followed in the new space')
      return
    }
    if (kept === undefined) {
      this.say('[xr] reset: no relation to keep — the machine stays at its coordinates')
      return
    }
    if (this.ritual !== undefined) {
      this.ritual.adopt(kept)
      this.rig.placement = kept
      // Written down again once the player reaches for it: the coordinates changed, the place did not.
      this.remembered = false
    } else if (this.venue !== undefined) {
      this.standVenueAt(kept)
    } else {
      return
    }
    this.sayPlacement('kept across the reset', '[xr]')
  }

  /**
   * The runtime moved its floor across the reset; the player did not (15 Sep 2026). The 23:47
   * pair: a standing player at 1.665 m read 1.23 m afterwards and the playfield stood at their
   * eyes. `FloorAlignment.holdContinuity` says how the step is applied.
   */
  private floorAcrossReset(eye: number): void {
    const before = this.eyeBeforeReset
    this.eyeBeforeReset = undefined
    if (before === undefined || !believableFloor(before)) return
    const delta = eye - before
    if (Math.abs(delta) < EYE_CONTINUITY_MIN || Math.abs(delta) > EYE_CONTINUITY_MAX) return
    if (this.floorSpace?.holdContinuity(delta) !== true) return
    this.say(
      `[xr] floor: across the reset the eyes read ${before.toFixed(2)} m, then ${eye.toFixed(2)} m — the runtime moved its floor `
        + `${Math.abs(delta).toFixed(3)} m ${delta < 0 ? 'up' : 'down'}; put back`,
    )
    this.toast = 'Floor moved under you — put back.'
  }

  /** The machine's placement at the relation to `head` that `rememberWhereItStands` last kept. */
  private placementFromRelation(head: { x: number; z: number; yaw: number }): Placement | undefined {
    const rel = this.headRelation
    if (rel === undefined) return undefined
    const at = headPoseOf(head)
    const p = toVenue(rel.at, at)
    return { x: p.x, y: p.y, z: p.z, yaw: yawToVenue(rel.yaw, at), scale: rel.scale }
  }

  /**
   * Keep the machine's relation to the head, and the eye height the headset is worn at, while no
   * recentre is pending — the frame before a reset is what `keepAcrossReset` restores.
   */
  private rememberWhereItStands(input: XRFrameInput): void {
    const head = input.head
    if (head === undefined || this.recentreWhy !== undefined) return
    const placement = this.build.active ? this.buildReturn : this.rig.placement
    if (placement !== undefined) {
      const at = headPoseOf(head)
      this.headRelation = { at: toPlayfield(placement, at), yaw: yawToPlayfield(placement.yaw, at), scale: placement.scale }
    }
    if (this.build.active && this.build.phase === 'testing') {
      const test = this.rig.placement
      const at = headPoseOf(head)
      this.testRelation = { at: toPlayfield(test, at), yaw: yawToPlayfield(test.yaw, at), scale: test.scale }
    } else this.testRelation = undefined
    if (this.hands.left.present || this.hands.right.present) this.wornEye = head.y
  }

  /**
   * Whether each hand was pinched on the last ritual frame — **starting closed, deliberately**
   * (`CabinetGrab`, `PartCarry`): a hand nobody has seen yet might already be shut, and the first
   * frame of one must not be an edge. An absent hand is remembered as closed for the same reason.
   */
  private readonly ritualPinchWas = { left: true, right: true }

  /** A pinch this frame from a hand that was not pinched last frame. Advances the latch. */
  private freshPinch(): boolean {
    let edge = false
    for (const hand of [this.hands.left, this.hands.right]) {
      const now = hand.present && hand.grab
      if (now && !this.ritualPinchWas[hand.handedness]) edge = true
      this.ritualPinchWas[hand.handedness] = hand.present ? hand.grab : true
    }
    return edge
  }

  private advanceRitual(input: XRFrameInput, frameSeconds: number): void {
    const ritual = this.ritual
    if (ritual === undefined) return
    // **The head goes in before anything else asks a question about it.** Recenter, a lost anchor
    // and the reach clamp are all answers about where the player is, and each of them can be
    // reached from a menu press on a frame nobody passed a head into.
    const head = input.head
    let stood = false
    if (head !== undefined) {
      const forward = head.forward ?? forwardFromYaw(head.yaw, head.pitch ?? 0)
      ritual.observe(head.x, head.y, head.z, forward.x, forward.y, forward.z)
      // **The first head pose is the first placement** (XR-002c). Once, and the ritual keeps the
      // once: from here the machine moves when the player asks — a recentre, a place-page row, a
      // hand — and not when they look somewhere. A remembered anchor that arrives afterwards
      // overtakes this the way it overtook the reticle before.
      stood = ritual.stand()
      if (stood && this.anchor === undefined && this.comfortOn) ritual.adopt(this.seatedRitualStand(ritual, head))
    }
    this.locateRestored(input)
    if (this.pending !== undefined) {
      // A restore is being located. The machine still stands where the head put it in the
      // meantime, rather than at the origin: nobody is left staring at nothing while a promise
      // that may be rejected settles.
      if (stood && this.anchor === undefined) {
        this.placeMachine(ritual.placement)
        this.sayStood()
      }
      return
    }
    ritual.onSurface(this.aim(input, frameSeconds))
    // **Said once, because a refused surface and an empty room look identical from here.** Both
    // leave the ritual scanning with no reticle drawn, and a player who is not told the difference
    // pinches at the same desk until they conclude the application is broken. Cleared the moment
    // they aim somewhere the machine would fit, so it is a hint rather than an alarm.
    if (ritual.aimedTooHigh !== this.saidTheAimIsTooHigh) {
      this.saidTheAimIsTooHigh = ritual.aimedTooHigh
      if (ritual.aimedTooHigh) this.toast = AIM_TOO_HIGH_LINE
    }
    // A pinch with either hand places it, which is the same gesture as grabbing the plunger and
    // is why the ritual's `startPlaying` is checked *after* the rig has run: reaching for the
    // machine beats aiming at the floor. **A fresh pinch** — the closing edge of a hand seen open
    // — and not the pinched state: a relaxed hand reads as pinched (`[xr] hands` at the start of
    // every session today: `L hands pinch fist | R hands pinch`), and read as a state it placed
    // the machine on the first frame the reticle existed, wherever the gaze happened to land —
    // "the pinball machine moved away from the user at the beginning".
    const fresh = this.freshPinch()
    if (ritual.phase === 'aiming' && fresh) {
      if (ritual.place()) this.toast = ritual.view().fit.advice
    }
    if (this.anchor === undefined) this.placeMachine(ritual.placement)
    if (stood) this.sayStood()

    // Written down once the player stops adjusting and starts playing, not on the frame they
    // placed it: the corner handles are live in between, and remembering the pose before they
    // have finished turning it would remember a pose they were part-way through changing.
    if (ritual.phase === 'playing' && !this.remembered) {
      this.remembered = true
      void this.anchorHere(input, ritual.placement, ritual.view().scaleMode)
    }
  }

  /**
   * Apply the saved seated choice only when the ritual deliberately stands at the head.
   * Restored anchors and placements chosen on a surface keep their own geometry.
   */
  private seatedRitualStand(ritual: PlacementRitual, head: NonNullable<XRFrameInput['head']>): Placement {
    return seatedPlacement(ritual.placement, head.x, head.y, head.z, head.yaw,
      this.options.dimensions.lockdownBarHeight, this.options.dimensions.length,
      knownEyeHeight(this.storage), heightDropOffset(readHeightOffset(this.storage)), true)
  }

  /**
   * What the reticle is on this frame — the room if it has an answer, the floor plane if not.
   *
   * **The substitution is only reached when the room is not going to answer.** A granted probe
   * that has never found anything is the case that matters: on a headset with no saved Space
   * Setup the runtime hands out a working hit-test source and then reports nothing for the whole
   * session, because it has no room to test against. `SurfaceSource` is what waits the three
   * seconds before concluding that, and what hands the room straight back the moment it speaks.
   */
  private aim(input: XRFrameInput, frameSeconds: number): XRFrameInput['surface'] {
    const source = this.surfaces
    const head = input.head
    if (source === undefined || head === undefined) return input.surface
    const forward = head.forward ?? forwardFromYaw(head.yaw, head.pitch ?? 0)
    const result = source.advance(
      input.surface,
      input.surfaceProbe ?? 'pending',
      frameSeconds,
      head.x,
      head.y,
      head.z,
      forward.x,
      forward.y,
      forward.z,
    )
    return result.hit
  }

  private async anchorHere(
    input: XRFrameInput,
    placement: Placement,
    mode: RitualView['scaleMode'],
  ): Promise<void> {
    const anchor = await createAnchorAt(
      input.frame as unknown as XRAnchorFramePort,
      input.referenceSpace,
      placement,
    )
    this.anchor = anchor
    // The session, so the handle this overwrites is given back rather than orphaned (M9-54).
    await rememberPlacement(
      this.storage,
      placement,
      mode,
      anchor,
      this.options.session?.() as XRAnchorSessionPort | undefined,
    )
  }

  /**
   * Put the drawn machine where the placement says.
   *
   * **`−yaw`, because Three's Euler Y turns the other way** (2026-09-13). `Placement.yaw` is
   * defined in `placement.ts` — the front at yaw θ is `(−sin θ, cos θ)`, which is what `toRoomPoint`
   * and `toCabinetPoint` implement, what every press gate, the plunger, the shove and the corner
   * grab are tested in, what `yawFacing` means by "faces the player", and what `VenueFrame.cs`
   * mirrors by fixture. Three's `rotation.y = θ` sends local +Z to `(sin θ, cos θ)`: the same
   * rotation, the other way round. So for four milestones the picture and the hands were mirrors
   * of each other at any yaw but zero — a 5° nudge opened a 10° gap between the flipper button a
   * player saw and the one they could press — and it stayed invisible because a WebXR
   * `local-floor` space is oriented to the head at session start and every bay in the arcade has
   * yaw 0, exactly the blind spot `venueFrame.ts`'s own header records for the same sign in
   * another file. `placementReticle.ts` draws with the same sign, for the same reason.
   */
  private applyPlacement(): void {
    const p = this.rig.placement
    const machine = this.options.machine
    machine.position.set(p.x, p.y, p.z)
    machine.rotation.set(0, -p.yaw, 0)
    machine.scale.setScalar(p.scale)
    // The visible shove (M5-11) rides on top of the placement, in the machine's own frame, so a
    // rotated cabinet wobbles along its own axes rather than the room's.
    const offset = this.rig.spring.offset
    const room = toRoomPoint(p, offset.x, 0, offset.z, { x: 0, y: 0, z: 0 })
    machine.position.set(room.x, p.y, room.z)
    machine.rotation.y = -(p.yaw + offset.yaw)
    const miniature = this.build.active && (this.build.phase !== 'testing' || this.build.flip.outcome !== 'full')
    fitCabinetToFloor(machine, this.lookNow === 'vr' && !miniature ? 0 : undefined)
  }

  /**
   * The caps, the rail lamps and the shooter rod, drawn as the rig says they are being used
   * (`controlPicture.ts`, 14 Sep 2026). The rig has computed every button's depression since M5
   * and nothing drew it; the rod's `drawn` was slid here as two level parts along z until the rod
   * lay along the lane and grew a tip.
   *
   * **The plunger was measured and never drawn** until M8. `plungerGrab` tracked `drawn` from the
   * day it was written — "for the sound and the animation" — and the animation half existed in
   * neither client: a player could close a hand on the knob, pull it the full 55 mm, hear the pitch
   * rise, and watch the plunger stay exactly where it was.
   */
  private drawControls(): void {
    if (this.picture === undefined) this.picture = new ControlPicture(this.options.machine, this.rig.controls)
    this.picture.draw(this.rig)
  }

  private picture: ControlPicture | undefined

  /**
   * Move the machine for a seated player (06 §6). Called when comfort mode goes on.
   *
   * Also called when the height row changes, which is what makes that row feel like a control
   * rather than a setting: 06 §6 asks for the height to be adjustable, and an adjustment that only
   * took effect the next time comfort mode was switched on would be one nobody could aim.
   */
  reposition(head: { x: number; y: number; z: number; yaw: number }): void {
    // Build mode never moves the machine (06 §9); the rows that reach here are refused before it.
    if (this.build.active) return
    const placement = seatedPlacement(
      this.rig.placement,
      head.x,
      head.y,
      head.z,
      head.yaw,
      this.options.dimensions.lockdownBarHeight,
      // The machine's own depth, so the gap it leaves in front of the player is the same on a
      // cabinet of any size and at any scale. See `seatedPlacement`: this used to be a flat 0.85 m
      // to the cabinet's *centre*, which left 0.20 m in front of a full-size one and 0.085 m in
      // front of a tabletop.
      this.options.dimensions.length,
      // This player's own eye height where one has been observed (M5-33). Only consulted when the
      // runtime's floor is not believable, which is exactly when a nominal person is the worst
      // available stand-in for a real one.
      knownEyeHeight(this.storage),
      // The player's own adjustment to the drop (G2-02). Read here rather than inside the
      // placement, which owns geometry and no storage.
      heightDropOffset(readHeightOffset(this.storage)),
      this.comfortOn,
    )
    // Carry the VR floor once with this explicit height change; MR keeps its real floor.
    if (this.venue !== undefined) this.standVenueAt(placement)
    else this.rig.placement = placement
  }

  /**
   * Put the panel on the left wrist and tell it what to say.
   *
   * Built lazily on the first frame the menu opens, so a player who never opens it never pays for
   * a canvas, a texture and a draw call. Positioned from the palm joint rather than parented to
   * anything: the hand tracker already has the pose, and a scene-graph parent would mean holding
   * a reference to an input source that the runtime replaces whenever tracking blinks.
   */
  /**
   * Put this frame's sentence in front of the player, or take the panel down (M9-47).
   *
   * **A toast is one frame and a caption is a duration, and the two are held differently.**
   * `advance` clears `this.toast` at the top of every frame, so a panel that drew what it was
   * handed would flash for 14 ms; a toast is therefore latched for `NOTICE_TOAST_SECONDS`. A
   * caption arrives from the audio layer already carrying its own dwell — the layer knows how long
   * the line it captions lasts — so holding it here as well would leave the last thing the machine
   * said hanging over the next thing.
   *
   * **A toast wins while it is running.** A recycle countdown and a lost anchor are things the
   * player has to act on; a caption is a thing they are welcome to miss. `audioCaptions` gates the
   * caption and not the toast, for the same reason: turning captions off is a statement about the
   * machine's speech, not about the product's warnings.
   */
  private drawNotice(input: XRFrameInput, frameSeconds: number): void {
    if (this.toast !== undefined) {
      this.noticeHolding = this.toast
      this.noticeLeftSeconds = NOTICE_TOAST_SECONDS
    } else if (this.noticeLeftSeconds > 0) {
      this.noticeLeftSeconds -= frameSeconds
      if (this.noticeLeftSeconds <= 0) this.noticeHolding = undefined
    }

    const caption = this.audioCaptions ? this.caption : undefined
    const say = this.noticeHolding ?? (caption !== undefined && caption.length > 0 ? caption : undefined)

    // Nothing to say and nothing built: the ordinary frame, and it allocates nothing.
    if (say === undefined && this.notice === undefined) return
    if (this.notice === undefined) {
      this.notice = buildNoticePanel()
      this.options.scene.add(this.notice.group)
    }

    const head = input.head
    const forward = head?.forward
    this.notice.update(
      say,
      head === undefined
        ? undefined
        : {
            x: head.x,
            y: head.y,
            z: head.z,
            // **The forward vector where there is one, the yaw where there is not.** M5-33's
            // reasoning, applied a second time: every caller written before it supplies a yaw and
            // nothing else, and a vector has no convention to disagree about. The yaw form is the
            // same one `reposition` uses, so a panel and a machine placed from the same head end
            // up on the same bearing.
            fx: forward?.x ?? -Math.sin(head.yaw),
            fy: forward?.y ?? 0,
            fz: forward?.z ?? -Math.cos(head.yaw),
          },
      frameSeconds,
    )
  }

  private readonly palmSession = new PalmSession()
  private readonly navigationRelease = new NavigationPointerRelease()
  private readonly navigationNormal = new Vector3()
  private menuFeedback: string | undefined
  private controllerMenuWasHeld = false
  private suppressQuickUntilLower = false
  private menuPauseRequested = false
  /**
   * The test is frozen because a menu was opened over it, not by the author's Freeze (1.0.245, the
   * owner's choice: "Resume unless I froze it"): "Return to test" runs it again. `NormalMenuFlow.cs`
   * is the twin.
   */
  private testFrozenForMenu = false
  private menuTrackingVisible = true
  private menuNeedsReposition = false
  private buildResumeHands: ('left' | 'right')[] | undefined
  private readonly buildResumeReleased = new Set<'left' | 'right'>()
  private readonly buildResumeReady = new Set<'left' | 'right'>()
  private quickCentre: {x:number;y:number;z:number} | undefined
  private quickDrawTime = 0
  private menuPosePresentation: MenuPresentation | undefined
  private menuStand: { bottom: MutablePoint; yaw: number; placement: Placement; scale:number; compact:boolean; belowCentre:number; aboveCentre:number } | undefined
  private menuStandFor(): { bottom: MutablePoint; yaw: number; placement: Placement; scale:number; compact:boolean; belowCentre:number; aboveCentre:number } | undefined {
    const head=this.lastHead; if(head===undefined)return undefined
    const p=this.rig.placement, old=this.menuStand?.placement
    if(old===undefined || old.x!==p.x || old.y!==p.y || old.z!==p.z || old.yaw!==p.yaw || old.scale!==p.scale) {
      const bottom=menuStandBottom(this.options.dimensions,p,head)
      const yScale=this.options.renderer.xr.getCamera?.()?.projectionMatrix.elements[5]
      const fov=yScale!==undefined&&yScale>0?2*Math.atan(1/yScale)*180/Math.PI:80
      this.menuStand={bottom,yaw:Math.atan2(head.x-bottom.x,head.z-bottom.z),placement:{...p},
        ...normalMenuShape(bottom,head,head.forward??forwardFromYaw(head.yaw,head.pitch??0),fov)}
      this.menuPosePresentation=undefined
      this.panel?.release();this.menuContactContext=undefined;this.menuPinchWasDown=true
      this.machineGallery?.cancelContact()
      this.say(`[xr] menu stand: cabinet height ${bottom.y.toFixed(3)}, opening pose fixed`)
    }
    return this.menuStand
  }
  private positionMenuGallery(): void {
    const stand=this.menuStandFor(), gallery=this.machineGallery
    if(stand===undefined || gallery===undefined)return
    gallery.group.position.set(stand.bottom.x,stand.bottom.y+.16625,stand.bottom.z)
    gallery.group.rotation.set(0,stand.yaw,0);gallery.group.updateMatrixWorld(true)
  }
  private menuContactContext: string | undefined
  /** Is the pointing fingertip over a row? Builder must not treat that hand as a part grab. */
  private menuEngaged = false
  private machineGallery: MachineGallery | undefined
  private galleryDetail: ((request: GalleryDetailRequest) => GalleryDetail) | undefined
  private galleryLoading = false
  private galleryEpoch = 0
  private galleryOpeningClosed: (() => void) | undefined
  private cancelGalleryOpening(): void {
    this.galleryEpoch++; this.galleryLoading = false
    const closed = this.galleryOpeningClosed; this.galleryOpeningClosed = undefined; closed?.()
  }

  /** Explicit normal menu ownership, independent of a raised left hand. No automatic resume. */
  openFullMenu(page: MenuPage = 'root'): boolean {
    if (this.pendingDeparture !== undefined && page !== 'departure') return false
    if(this.accountUtilityPending){this.toast='Leaving VR for Account…';return false}
    if (this.lastHead === undefined) { this.toast = 'Head tracking is needed to place the full menu.'; return false }
    if (this.menu.open && this.menu.presentation === 'normal' && this.menu.view().page === page) return true
    this.pauseForMenu()
    this.suppressQuickUntilLower = true
    this.menu.openNormal(page)
    this.menuPosePresentation = undefined; this.menuContactContext = undefined
    this.panel?.release(); this.menuPinchWasDown = true
    this.rig.releaseAll()
    return true
  }

  private consumeMenuPointer(buttonSelection = false): void {
    const pointer=this.hands.right
    if(this.panel===undefined||!pointer.present)return
    this.panel.group.getWorldDirection(this.navigationNormal)
    this.navigationRelease.consume(pointer.tip,this.navigationNormal,buttonSelection||pointer.kind==='controllers')
  }

  private completeNormalTask(task: string): void {
    if(!this.menu.open||this.menu.presentation!=='normal')return
    this.consumeMenuPointer(this.acceptedMenuButton)
    this.closeNormalMenu();this.suppressQuickUntilLower=true
    this.say(`[xr] menu task: ${task} handed off; pointer-ready=${!this.navigationRelease.blocked}`)
  }

  private acceptedMenuButton=false
  private accountUtilityPending=false
  private accountUtilitySuspended=false

  private closeNormalMenu(): void {
    this.pendingDeparture = undefined
    this.menu.closeNormal(); this.panel?.release(); this.menuPinchWasDown = true
    this.menuFeedback = undefined; this.menuPosePresentation = undefined
  }

  /** "Return to test" runs a test the menu froze; a freeze the author chose is kept (1.0.245). */
  private resumeATestTheMenuFroze(): void {
    if (!this.testFrozenForMenu) return
    this.testFrozenForMenu = false
    const tools = this.options.build?.tools
    if (tools === undefined || this.build.phase !== 'testing' || !tools.frozen()) return
    tools.freeze()
    this.say('[build] returned to the test; it runs again (it was frozen for the menu, not by Freeze)')
  }

  private pauseForMenu(): void {
    if (!this.build.active && !this.gameInProgress) return
    if (this.build.active && this.build.phase !== 'testing') return
    const tools = this.build.phase === 'testing' ? this.options.build?.tools : undefined
    const paused = tools !== undefined ? tools.frozen() : this.gamePaused
    if (paused || this.pausedByVisibility || this.menuPauseRequested) return
    if (tools !== undefined) { tools.freeze(); this.testFrozenForMenu = true }
    else if (this.options.onMenu !== undefined) this.options.onMenu({ item: 'pauseResume', confirmed: false })
    else { this.menuFeedback = 'Pause is unavailable in this preview.'; return }
    this.menuPauseRequested = true
  }

  private showReadingPanel(kind: 'rules' | 'tutorial'): void {
    const head = this.lastHead, definition = this.options.build?.definition()
    if (head === undefined || definition === undefined) {
      this.menuFeedback = 'This machine’s instructions are unavailable. Return to the full menu.'
      this.menu.openNormal('help'); return
    }
    this.pauseForMenu()
    const returnPage: MenuPage = 'root' // The reading control explicitly says Back to Full menu on both clients.
    this.showMachineGallery(definition.machineId, () => undefined, () => { this.openFullMenu(returnPage) }, {
      kind, mode: this.build.active ? this.build.phase === 'testing' ? 'test' : 'build' : 'play',
      machineId: definition.machineId, machineVersion: definition.machineVersion,
      machineTitle: definition.meta.title, ballsPerGame: definition.meta.ballsPerGame,
    })
  }

  /** The picture browser is one modal input owner; its shelf stays still while reaching. */
  showMachineGallery(current: string, picked: (id: string) => void, closed: () => void,
    detail?: GalleryDetailRequest): boolean {
    if (!this.presenting || this.lastHead === undefined) return false
    if (this.machineGallery?.visible || this.galleryLoading) return true
    const head = { ...this.lastHead }
    this.menu.close(); this.panel?.release(); this.menuPinchWasDown = true
    this.suppressQuickUntilLower = true
    if (this.panel !== undefined) this.panel.group.visible = false
    this.rig.releaseAll()
    const show = (gallery: MachineGallery): void => {
      if (detail === undefined) gallery.show(head, current, picked, closed)
      else gallery.showDetail(head, current, this.galleryDetail!(detail), closed)
      this.positionMenuGallery()
    }
    if (this.machineGallery !== undefined && this.galleryDetail !== undefined) {
      show(this.machineGallery)
      return true
    }
    this.galleryLoading = true; this.galleryOpeningClosed = closed
    const epoch = ++this.galleryEpoch
    // Never imported by constructing XRRuntime or entering a headset. This method is called
    // only by explicit Arcade, Rules, or tutorial actions after the player asks to read/browse.
    void import('../world/machineGallery').then(({ MachineGallery: Gallery, galleryDetailFor }) => {
      if (epoch !== this.galleryEpoch || !this.presenting) return
      this.machineGallery = new Gallery(); this.options.scene.add(this.machineGallery.group)
      this.galleryDetail = galleryDetailFor
      show(this.machineGallery)
      this.galleryLoading = false; this.galleryOpeningClosed = undefined
    }).catch(() => {
      if (epoch !== this.galleryEpoch) return
      this.cancelGalleryOpening()
      if (detail === undefined) this.toast = 'The arcade could not open. Try Arcade again.'
      else {
        this.openFullMenu('help')
        this.menuFeedback = 'Instructions could not open. Choose Rules or Show me how to retry.'
      }
    })
    return true
  }

  private drawMenu(): void {
    if(this.accountUtilityPending){this.panel?.release();if(this.panel)this.panel.group.visible=false;this.menuEngaged=false;return}
    if (this.machineGallery?.visible || this.galleryLoading) {
      this.positionMenuGallery()
      this.menu.close()
      if (this.panel !== undefined) this.panel.group.visible = false
      this.machineGallery?.setInputHint(this.navigationRelease.blocked ? this.navigationRelease.buttonSelection
        ? 'Release your pinch or trigger to continue' : 'Pull your right hand back, then tap' : undefined)
      if (!this.navigationRelease.blocked && this.menuTrackingVisible && this.lastHead !== undefined) this.machineGallery?.advance(this.hands.right)
      else this.machineGallery?.cancelContact()
      return
    }
    if (!this.menu.open && this.panel === undefined) {
      this.menuEngaged = false
      return
    }
    if (!this.menu.open) {
      this.panel?.release(); if (this.panel !== undefined) this.panel.group.visible = false
      this.menuEngaged = false; this.menuPosePresentation = undefined; this.menuContactContext = undefined
      return
    }
    if (this.panel === undefined) {
      this.panel = buildWristPanel()
      this.options.scene.add(this.panel.group)
    }
    const view = this.menu.view(), presentation = view.presentation ?? 'normal'
    const head = this.lastHead
    const stand = head === undefined ? undefined : this.menuStandFor()
    if ((this.menuPosePresentation !== presentation || presentation==='quick' && this.palmSession.interactive) && stand !== undefined) {
      const scale = presentation === 'quick' ? QUICK_MENU_SCALE : stand.scale
      this.panel.group.scale.setScalar(scale)
      let centre=presentation==='quick'
        ? quickMenuCentre(this.options.dimensions,this.rig.placement,head!,this.hands.left.palm,{x:Math.cos(head!.yaw),y:0,z:Math.sin(head!.yaw)})
        : {x:stand.bottom.x,y:stand.bottom.y+(stand.compact?stand.belowCentre:.11*scale),z:stand.bottom.z}
      const dt=Math.max(0,this.elapsed-this.quickDrawTime),opening=this.menuPosePresentation!==presentation
      this.quickDrawTime=this.elapsed
      if(presentation==='quick'){
        if(!opening&&this.quickCentre!==undefined)centre=followQuick(this.quickCentre,centre,head!,this.hands.left.palm,dt)
        this.quickCentre=centre
      }
      let yaw=presentation==='quick'?Math.atan2(head!.x-centre.x,head!.z-centre.z):stand.yaw
      if(presentation==='quick'&&!opening){const before=this.panel.group.rotation.y;const delta=Math.atan2(Math.sin(yaw-before),Math.cos(yaw-before));yaw=before+delta*dt/(.06+dt)}
      // The web quad has a local +Z offset; compensate once so the actual face agrees with native.
      this.panel.group.position.set(centre.x-Math.sin(yaw)*TOWARD_VIEWER*scale,
        centre.y,centre.z-Math.cos(yaw)*TOWARD_VIEWER*scale)
      this.panel.group.rotation.set(0, yaw, 0)
      this.panel.group.updateMatrixWorld(true)
      this.menuPosePresentation = presentation
    }
    // Quick follows the holding hand; Normal retains its cabinet-owned pose.
    const state = this.menuState(this.menuOverWas)
    this.panel.update(view, state, this.menuOverWas,this.menuTouchStatus())
    const surface = `${presentation}:${stand?.compact}:${view.page}:`
    const context = surface + contextRows(view,state).join(',')
    if (context !== this.menuContactContext) {
      // A live inventory change can move a different command under an approaching finger.
      // Resetting the tap gate alone rearms on the next hover sample; require a fresh
      // withdrawal or button release before this same surface can accept its new rows.
      if (this.menuContactContext?.startsWith(surface)) {
        const pointer = this.hands.right
        this.consumeMenuPointer(pointer.grab && pointer.grabSource === 'runtime')
      }
      this.panel.release(); this.menuPinchWasDown = true; this.menuContactContext = context
    }
    if (!this.menuTrackingVisible || head === undefined || presentation === 'quick' && !this.palmSession.interactive) {
      this.panel.release(); this.menuPinchWasDown = true; this.menuEngaged = false
      return
    }

    if (this.navigationRelease.blocked) {
      this.panel.release(); this.menuPinchWasDown = true; this.menuEngaged = true
      return
    }

    // The *other* hand points at it. A player cannot press a button on their own wrist with the
    // hand it is attached to, and trying is the kind of thing that only shows up on a device.
    const pointer = this.hands.right
    const over = pointer.present
      ? this.panel.itemAt(pointer.tip.x, pointer.tip.y, pointer.tip.z)
      : undefined
    this.menuEngaged = this.menu.open && over !== undefined

    // **A hand taps a row; a controller squeezes on it.** The pinch was the one gesture for both,
    // latched on its edge — and a relaxed hand reads as pinched, so for a hand the edge had
    // already happened before the finger reached the row (`WristPanelBuild.press`). A controller's
    // squeeze is a button, and a button's edge is a decision. A destructive item returns nothing
    // on the first press and arms instead (06 §5), whichever way it was pressed.
    let pressed: MenuItem | undefined
    let buttonSelection = false
    if (pointer.present && pointer.kind === 'hands') {
      pressed = this.panel.press(pointer.tip.x, pointer.tip.y, pointer.tip.z, this.elapsed, presentation === 'quick')
      // **And a pinch over a row, when the pinch is the headset's own** (13 Sep 2026). The tap
      // replaced the pinch because the ruler read a relaxed hand as pinched, so its edge was spent
      // before the finger arrived. With `grabSource === 'runtime'` the edge is the headset's pinch
      // detector — the gesture every button in its own interface takes — and an author who points
      // at a row and pinches, as the headset has taught them to, is pressing it. The tap still
      // works; this is the second way in, for a hand the events have named.
      const pinching = pointer.grab && pointer.grabSource === 'runtime'
      if (pressed === undefined && over !== undefined && pinching && !this.menuPinchWasDown) {pressed = over;buttonSelection = true;this.panel.release()}
      this.menuPinchWasDown = pinching
    } else {
      this.panel.release()
      const pinching = pointer.present && pointer.trigger
      if (over !== undefined && pinching && !this.menuPinchWasDown) { pressed = over; buttonSelection = true }
      this.menuPinchWasDown = pinching
    }
    // Said once per change, so a press that never came is answered by where the finger was:
    // over which row, or off the panel — the 22:48 session had a menu open for nineteen seconds,
    // a finger somewhere, and no row pressed.
    // And when the row the finger just left was never pressed, how near the picture it came
    // there (23:55, 13 Sep 2026): a finger that pushed at a row and got nothing reads
    // "nearest 4 mm in front"; one that swept past reads 30 mm. Read every frame so a record
    // made while the menu was closing does not attach to the next line.
    const left = this.panel.takeLastLeave()
    if (over !== this.menuOverWas) {
      this.menuOverWas = over
      if (this.menu.open) {
        const unpressed =
          left === undefined
            ? ''
            : ` (left row ${left.row + 1} of ${left.rows} unpressed; nearest ${Math.abs(Math.round(left.nearest * 1000))} mm ${left.nearest >= 0 ? 'in front of' : 'behind'} the picture)`
        this.say(`[xr] menu: pointer ${over === undefined ? 'off the panel' : `over ${over}`}${unpressed}`)
      }
    }
    if (pressed !== undefined) {
      const unavailable=unavailableReason(pressed,this.menuState())
      if(unavailable!==undefined){this.menuFeedback=unavailable;this.toast=unavailable;return}
      this.menuTouchAcceptedUntil=this.elapsed+.35
      const chosen = this.menu.select(pressed)
      if (chosen !== undefined) {
        this.onMenuSelection(chosen,buttonSelection)
      } else {
        // **A page change is a press too, and the console says so** (23:55, 13 Sep 2026). The
        // menu answers `select` with nothing for `back`, `machine`, `scaleAndPlace`, `sound`,
        // `comfort` and a first press on a destructive row, so for an hour that evening six
        // presses that had worked read as "the row lit and nothing happened".
        const view = this.menu.view()
        const what = view.armed === pressed ? 'armed — press again to confirm' : `→ ${view.page} page`
        this.say(`[xr] menu: ${pressed} pressed ${what}${this.pressWhere()}`)
        // The warning before a reset (the 1.0.115 round): what the second press does, and that
        // Undo takes it back, in a sentence as well as on the row.
        if (view.armed === 'resetMachine' && pressed === 'resetMachine') this.toast = this.build.strings['toast.resetArmed']
      }
    }

    this.panel.update(this.menu.view(), this.menuState(over), over,this.menuTouchStatus())
  }

  private historyWords(value:string):string {
    const words=value.replace(/([a-z])([A-Z])/g,'$1 $2').replace(/[_-]/g,' ')
    return words.charAt(0).toUpperCase()+words.slice(1)
  }

  /** Input owner supplies touch state; the atlas changes UVs, never repaints its words. */
  private menuTouchStatus(): 'accepted'|'release'|undefined {
    return this.elapsed<this.menuTouchAcceptedUntil?'accepted':this.menuPinchWasDown||this.panel?.held()!==undefined?'release':undefined
  }

  private menuState(highlighted?: MenuItem): MenuState {
    const definition = this.options.build?.definition()
    const pending = this.navigationRelease.blocked ? this.navigationRelease.buttonSelection ? 'Release your pinch or trigger to continue' : 'Pull your right hand back, then tap'
      : this.menuFeedback ?? (highlighted === 'account' ? 'Account opens on screen and leaves VR.' : undefined)
    return {
        // Either way of being stopped. A row that means "is this machine running" has to
        // answer for both, and only one of them is ever visible to a player.
        paused: this.pausedByVisibility || this.gamePaused,
        gameInProgress: this.gameInProgress,
        buildDeparture: this.pendingDeparture !== undefined,
        soundMuted: this.audioMuted,
        master: this.audioMaster,
        captions: this.audioCaptions,
        micMuted: this.voice.mute.microphone,
        micAccess: this.voice.access,
        voiceMuted: this.voice.mute.others,
        voiceAvailable: this.venuePresence !== undefined,
        ...(definition === undefined ? {} : { currentMachineTitle: definition.meta.title }),
        ...(pending === undefined ? {} : { pendingMenuDetail: pending }),
        bays: this.bayLabels(),
        // The ritual's own answer, not a copy kept beside it: the scale row states a condition and
        // a condition read from a stale mirror is the mute-that-lies defect wearing a different hat.
        tabletop: this.ritual?.view().scaleMode === 'tabletop',
        enteringInitials: this.enteringInitials,
        // The comfort rows (G2-03). Read from the runtime's own flag and from storage rather than
        // from a copy kept beside them, for the reason the `tabletop` line above gives: a row that
        // states a condition and reads it from a stale mirror is the mute-that-lies defect wearing
        // a different hat.
        seated: this.comfortOn,
        compactMenu:this.menuStand?.compact===true,
        seatingChoiceKnown: this.seatingChoiceKnown,
        flashing: readFlashingChoice(this.storage),
        motion: motionStepOf(readMotionIntensity(this.storage)),
        heightOffset: readHeightOffset(this.storage),
        // Build mode's rows, only when the flag and the host both offer it (M13-09). Absent is
        // the menu exactly as it was before M13 — the parity fixture's own shape.
        roomAvailable: this.ritual!==undefined,
      ...(this.buildOffered
          ? {
              build: {
                active: this.build.active,
        unfinishedMove: this.unfinishedBuildEdit(),
                canUndo: this.build.canUndo,
                canRedo: this.build.canRedo,
                // What each would change, and whether Reset has anything to do (the 1.0.115 round).
                ...(this.build.undoWhat === undefined ? {} : { undoWhat: this.historyWords(this.build.undoWhat) }),
                ...(this.build.redoWhat === undefined ? {} : { redoWhat: this.historyWords(this.build.redoWhat) }),
                canReset: this.build.canReset,
                symmetry: this.build.symmetry,
                testing: this.build.phase === 'testing',
                language: this.build.language,
                // The test tools' states (M13-25), while a test runs.
                ...(this.build.phase === 'testing' ? this.tools.state(this.options.build?.tools?.frozen() === true) : {}),
                ...(this.build.phase === 'testing' && this.testFrozenForMenu && this.options.build?.tools?.frozen() === true
                  ? { frozenForMenu: true } : {}),
              },
            }
          : {}),
        // The Switch row (14 Sep 2026), only while the runtime has the other mode to offer.
        ...(this.switchTo === undefined ? {} : { switchTo: this.switchTo }),
      }
  }

  /**
   * Perform a menu choice.
   *
   * The three that are *this* object's business are handled here; everything else goes out to the
   * shell, because pausing a game, changing the machine and muting the sound are all things this
   * class deliberately has no reference to (see the note at the top).
   */
  /**
   * The ritual, or a sentence saying why there is not one.
   *
   * Every row on the place page needs it, and a row that silently did nothing outside mixed
   * reality would be indistinguishable from a press the panel missed — which is the reason the
   * unwired rows on the root page report rather than pretend.
   */
  private needRitual(): PlacementRitual | undefined {
    if (this.ritual !== undefined) return this.ritual
    this.toast = 'Scale and placement need the room — they are for mixed reality.'
    return undefined
  }

  /**
   * Apply what a place-page row just changed, and stop the anchor arguing with it.
   *
   * `remembered` goes false so the new placement is written down once, the same as any other
   * change to it. The anchor is *kept*: these rows adjust a machine that is still where the
   * player put it, so its handle into the headset's map is still the right one. `replaceMachine`
   * and `forgetSpot` are the two that let go of it, and they say so themselves.
   */
  private afterPlacementChange(ritual: PlacementRitual, said: string | undefined): void {
    this.rig.placement = ritual.placement
    this.remembered = false
    if (said !== undefined) this.toast = said
  }

  /** Stop following the anchor, without forgetting what the player chose. */
  private forgetTheAnchor(): void {
    this.anchor = undefined
    this.pending = undefined
    this.unlocatedFrames = 0
  }

  /** Where the last wrist press landed, as a clause for the console, or '' for a controller. */
  private pressWhere(): string {
    const at = this.panel?.takeLastPress()
    if (at === undefined) return ''
    if (at.landed === undefined) return ` at row ${at.row + 1} of ${at.rows}, ${Math.round(at.down * 100)}% down the row`
    return (
      ` at row ${at.row + 1} of ${at.rows} — the finger landed ${Math.round(at.down * 100)}% down row ${at.landed + 1}`
      + ` only ${(at.after ?? 0).toFixed(2)} s after the highlight left row ${at.row + 1}, so the press went to the row that was lit`
    )
  }

  private onMenuSelection(selection: MenuSelection, buttonSelection = false): void {
    if(this.accountUtilityPending){this.toast='Leaving VR for Account…';return}
    // Said before anything acts on it, so a session that ends on this frame still names the row.
    // The 13 Sep 2026 evening: the menu closed on a press and the frame loop stopped on the same
    // frame, and nothing on the console said which of `exitXr` two rows above `build` or the
    // Quest's own system gesture had done it.
    this.say(`[xr] menu: ${selection.item} pressed${selection.confirmed ? ' (confirmed)' : ''}${this.pressWhere()}`)
    if (this.pendingDeparture !== undefined) {
      if (selection.item === 'back' || selection.item === 'close') {
        this.pendingDeparture = undefined
        this.completeNormalTask('cancel-departure')
        this.resumeATestTheMenuFroze()
      } else if (this.keeping) {
        this.menuFeedback = 'Waiting for Keep to finish.'
      } else if (selection.item === 'leaveBuild') {
        this.finishBuilderDeparture()
      } else if (selection.item === 'keep') {
        const reason = unavailableReason('keep', this.menuState())
        if (reason !== undefined) this.menuFeedback = this.toast = reason
        else if (this.build.draft?.definition === this.keptDefinition) this.finishBuilderDeparture()
        else {
          this.pendingDeparture.saving = this.build.draft?.definition
          this.keepTheDraft()
          if (!this.keeping) this.menuFeedback = this.toast
        }
      }
      return
    }
    if (selection.item === 'fullMenu' || selection.item === 'currentRules') {
      if (this.panel !== undefined && this.hands.right.present) {
        const pointer = this.hands.right
        this.panel.group.getWorldDirection(this.navigationNormal)
        this.navigationRelease.consume(pointer.tip, this.navigationNormal,buttonSelection)
      }
      if (selection.item === 'currentRules') this.showReadingPanel('rules')
      else this.openFullMenu()
      this.say(`[xr] menu: destination=${selection.item} requested; pointer-ready=${!this.navigationRelease.blocked}`)
      return
    }
    this.menuFeedback = undefined
    this.acceptedMenuButton=buttonSelection
    const unavailable=unavailableReason(selection.item,this.menuState())
    if(unavailable!==undefined){this.menuFeedback=unavailable;this.toast=unavailable;return}
    if (selection.item === 'close') { this.consumeMenuPointer(buttonSelection);this.closeNormalMenu();this.suppressQuickUntilLower=true;this.resumeATestTheMenuFroze();return }
    if (selection.item === 'help') { this.openFullMenu('help'); return }
    if (selection.item === 'tutorial') { this.showReadingPanel('tutorial'); return }
    if (this.build.active && (LEAVES_THE_MACHINE.has(selection.item) || bayOfItem(selection.item) >= 0 || selection.item === 'endGame' || selection.item === 'leaveBuild')) {
      if (this.unfinishedBuildEdit() || (this.build.canUndo && this.build.draft?.definition !== this.keptDefinition)) {
        this.pendingDeparture = {selection: {...selection}, button: buttonSelection, saving: undefined}
        this.consumeMenuPointer(buttonSelection)
        this.openFullMenu('departure')
        this.menuFeedback = this.unfinishedBuildEdit()
          ? 'Finish placing or cancel the held part before leaving Builder.'
          : 'Keep your draft first, or choose Leave Builder and confirm discarding edits.'
        this.toast = this.menuFeedback
        return
      }
    }
    if (this.build.active && this.unfinishedBuildEdit() && (selection.item === 'keep' || selection.item === 'testPlay' && this.build.phase !== 'testing')) {
      this.menuFeedback = 'Finish placing or cancel the held part before testing or keeping.'
      this.toast = this.menuFeedback; return
    }
    if (selection.item === 'account') {
      this.consumeMenuPointer(buttonSelection)
      this.accountUtilityPending=true
      this.pauseForMenu(); this.closeNormalMenu()
      void this.exit().then(() => this.options.onMenu?.(selection)).catch(() => {
        this.accountUtilityPending=false;this.accountUtilitySuspended=false
        this.openFullMenu(); this.menuFeedback = 'Could not leave VR for Account. Try again.'
      })
      return
    }
    // Build mode first (M13-09). Its own rows; the rows that would move the machine, refused
    // while it is on; and the rows that leave the machine, which end it on the way out.
    if (this.build.active && selection.item === 'recenterMachine') {
      // **The one movement row that means something at a bench** (13 Sep 2026): the bench is
      // stood relative to the head, so "bring it to me" is a re-bench — the recycle's path — and
      // not a refusal. Pressed four times across two sessions by an author who had walked round
      // the bench and wanted it back in front of them. Deferred until the head is up and still
      // (14 Sep 2026): from the head at the press it re-benched toward the wrist.
      this.askARecentre()
      return
    }
    if (this.build.active && MOVES_THE_MACHINE.has(selection.item)) {
      this.toast = this.build.strings['toast.leaveFirst']
      return
    }
    if (this.build.active && (LEAVES_THE_MACHINE.has(selection.item) || bayOfItem(selection.item) >= 0)) {
      this.leaveBuild()
    }
    const bay = bayOfItem(selection.item)
    if (bay >= 0) {
      this.closeNormalMenu()
      // The route to a machine the look cone cannot reach: the cone is 16° in front of the player,
      // so the far end of the row behind them has no gesture at all (06 §4).
      const statuses = bayStatuses(this.floor.bays.length, this.floor.index, this.occupants())
      this.goToBay(statuses[bay])
      return
    }
    switch (selection.item) {
      case 'build':
        this.enterBuild()
        return
      case 'undo':
      case 'redo': {
        this.dropTheCarry()
        const changed = selection.item === 'undo' ? this.build.undo() : this.build.redo()
        if (!changed) return
        this.adoptDraft()
        return
      }
      case 'symmetry':
        if (this.build.toggleSymmetry()) this.toast = this.build.strings['toast.symmetryOn']
        else this.toast = this.build.strings['toast.symmetryOff']
        return
      case 'testPlay':
        if (this.build.phase === 'testing') this.endTest()
        else {
          this.beginTest()
          if (this.menu.home === 'test') this.completeNormalTask('start-test')
        }
        return
      // The test tools (M13-25): each on the draft's session through the host, each said.
      case 'freeze': {
        const tools = this.options.build?.tools
        if (tools === undefined || this.build.phase !== 'testing') return
        const wasFrozen = tools.frozen()
        tools.freeze()
        this.testFrozenForMenu = false // The author's own Freeze or Run from here on.
        if (wasFrozen) this.completeNormalTask('run-test')
        this.menuPauseRequested = false
        this.toast = wasFrozen ? this.build.strings['toast.running'] : this.build.strings['toast.frozen']
        this.say(`[build] test tools: ${wasFrozen ? 'run' : 'freeze'}`)
        return
      }
      case 'step': {
        const tools = this.options.build?.tools
        if (tools === undefined || this.build.phase !== 'testing') return
        if (!tools.frozen()) {
          this.toast = this.build.strings['menu.stepFrozenFirst']
          return
        }
        if (tools.step()) {
          this.toast = this.build.strings['toast.stepped']
          this.say('[build] test tools: step +1 ms')
        }
        return
      }
      case 'slowMotion': {
        const tools = this.options.build?.tools
        if (tools === undefined || this.build.phase !== 'testing') return
        const speed = this.tools.nextSpeed()
        tools.slow(speed)
        this.toast = benchText(this.build.strings, 'toast.speed', { speed: speedText(speed) })
        this.say(`[build] test tools: speed ${speedText(speed)}x`)
        return
      }
      case 'placeBall': {
        if (this.options.build?.tools === undefined || this.build.phase !== 'testing') return
        const armed = this.tools.togglePlacing()
        if (armed) {this.toast = this.build.strings['toast.placeBall'];this.completeNormalTask('place-ball')}
        this.say(`[build] test tools: place ball ${armed ? 'armed' : 'disarmed'}`)
        return
      }
      case 'addBall': {
        if (this.options.build?.tools === undefined || this.build.phase !== 'testing') return
        const armed = this.tools.toggleAdding()
        if (armed) {this.toast = this.build.strings['toast.addBall'];this.completeNormalTask('add-ball')}
        this.say(`[build] test tools: add ball ${armed ? 'armed' : 'disarmed'}`)
        return
      }
      case 'trail': {
        const tools = this.options.build?.tools
        if (tools === undefined || this.build.phase !== 'testing') return
        const on = this.tools.toggleTrail()
        tools.ribbon(on)
        this.toast = on ? this.build.strings['toast.trailOn'] : this.build.strings['toast.trailOff']
        this.say(`[build] test tools: trail ${on ? 'on' : 'off'}`)
        return
      }
      case 'keep':
        this.keepTheDraft()
        return
      case 'resetMachine':
        this.resetTheDraft()
        return
      case 'leaveBuild':
        this.leaveBuild()
        return
      case 'pauseResume':
        if (!this.gameInProgress) { this.completeNormalTask('return-to-table'); return }
        if (this.gamePaused && this.menu.presentation === 'normal') this.completeNormalTask('resume-game')
        this.menuPauseRequested = false
        this.options.onMenu?.(selection)
        return
      case 'exitXr':
        this.closeNormalMenu()
        void this.exit()
        return
      case 'switchToVr':
      case 'switchToMr':
        // In place, the session untouched (15 Sep 2026): see `setLook`. Build is not left — the
        // draft stays on its bench while the room goes up or comes down around it.
        this.consumeMenuPointer(buttonSelection)
        this.setLook(selection.item === 'switchToVr' ? 'vr' : 'mr')
        this.panel?.release()
        if (this.menu.presentation === 'normal') this.completeNormalTask('switch-look')
        return
      // `comfort` itself no longer does anything — it pages (G2-03), and `PAGING` is consulted
      // before a selection is ever reported. `seatedMode` is the row it used to be.
      case 'seatedMode':
        this.setComfort(!this.comfortOn)
        this.toast = this.comfortOn
          ? 'Seated play on. Use hand controls or controller shortcuts.'
          : 'Seated play off. Cabinet stays here. Use Room & position to move it.'
        return
      case 'reduceFlashing': {
        // **Through the module that owns the preference, not through the XR layer**, which is the
        // shape M8-23 established for the Sound rows: the row asks for a change, the owner makes
        // it, and every surface reading that owner sees the same answer on its next frame. A
        // runtime that kept its own copy would be a second opinion about a setting whose whole
        // purpose is that it applies everywhere.
        const chosen = cycleFlashing(this.storage)
        this.toast = flashingLabel(chosen) + '.'
        return
      }
      case 'reduceMotion': {
        const step = cycleMotionPreference(this.storage)
        this.toast = motionLabel(step) + '.'
        return
      }
      case 'heightOffset': {
        const chosen = cycleHeightOffset(this.storage)
        this.toast = heightLabel(chosen) + '.'
        // **Re-placed immediately, which is what makes this a control rather than a setting.** An
        // adjustment that only took effect the next time comfort mode was switched on is one
        // nobody could aim: a player presses the row to move the machine, looks at the machine, and
        // presses again. Only where there is a head to place against — with none, the next
        // `reposition` picks the new offset up anyway.
        if (this.lastHead !== undefined) this.reposition(this.lastHead)
        return
      }
      case 'scaleMode': {
        const ritual = this.needRitual()
        if (ritual === undefined) return
        const next = ritual.view().scaleMode === 'full' ? 'tabletop' : 'full'
        ritual.setScaleMode(next)
        this.afterPlacementChange(ritual, next === 'full' ? 'Full size.' : 'Tabletop.')
        return
      }
      case 'faceMe': {
        const ritual = this.needRitual()
        const head = this.lastHead
        if (ritual === undefined) return
        if (head === undefined) {
          this.toast = 'Not sure where you are yet — try again in a moment.'
          return
        }
        ritual.faceMe(head.x, head.z)
        this.afterPlacementChange(ritual, 'Turned to face you.')
        return
      }
      case 'recenterMachine': {
        // In VR the venue answers (14 Sep 2026); in MR the ritual; in neither, the sentence.
        if (this.venue === undefined && this.needRitual() === undefined) return
        // Deferred until the head is up and still (14 Sep 2026): from the head at the press the
        // machine stood toward the wrist the row was pressed on.
        this.askARecentre()
        return
      }
      case 'nudgeLeft':
      case 'nudgeRight': {
        const ritual = this.needRitual()
        if (ritual === undefined) return
        ritual.rotateBy(selection.item === 'nudgeLeft' ? -NUDGE_RADIANS : NUDGE_RADIANS)
        this.afterPlacementChange(ritual, undefined)
        return
      }
      case 'replaceMachine': {
        const ritual = this.needRitual()
        if (ritual === undefined) return
        // **The anchor goes with it.** An anchor still tracking drags the cabinet back to
        // yesterday's spot on the very next frame, which is the same rule a refused restore obeys.
        ritual.replace()
        this.closeNormalMenu()
        this.forgetTheAnchor()
        this.toast = 'Look where you want the machine and pinch.'
        return
      }
      case 'forgetSpot': {
        const ritual = this.needRitual()
        if (ritual === undefined) return
        // Everything, not just the position: the scale and the angle were chosen for a spot that
        // is being thrown away, which is what makes this the destructive one and `replaceMachine`
        // not.
        ritual.replace()
        this.closeNormalMenu()
        ritual.setScaleMode('full')
        this.forgetTheAnchor()
        // The persistent entry too (M9-54), and before the record that names it goes.
        void releaseAnchor(this.options.session?.() as XRAnchorSessionPort | undefined, rememberedHandle(this.storage))
        forgetAnchor(this.storage)
        this.remembered = false
        this.toast = 'Forgotten. Look where you want the machine and pinch.'
        return
      }
      default:
        if (selection.item === 'otherMachine' || selection.item === 'endGame') this.closeNormalMenu()
        this.options.onMenu?.(selection)
    }
  }

  private suspendedWorldInput():boolean {return this.unfinishedBuildEdit()||this.tools.holding}

  private unfinishedBuildEdit(): boolean {
    return this.build.active && (this.build.carry.carrying || this.build.shaper.holding !== undefined || this.build.inspector.holding !== undefined)
  }

  // --- Build mode (M13) ---------------------------------------------------------------

  /**
   * Where an ordinary path would put the machine, applied — or, while Build mode is on, kept for
   * when it ends (M13-10).
   *
   * The ritual, a restored anchor and the venue's bay each write the placement, and each would
   * otherwise pull the bench out from under the author — the ritual every frame. Build mode is a
   * temporary owner: the writes are not lost, they are what `leaveBuild` returns to.
   */
  private placeMachine(placement: Placement): void {
    if (this.build.active) this.buildReturn = placement
    else this.rig.placement = placement
  }

  /** The cabinet as the bench needs it. */
  private benchCabinet(): { length: number; lockdownBarHeight: number } {
    return {
      length: this.options.dimensions.length,
      lockdownBarHeight: this.options.dimensions.lockdownBarHeight,
    }
  }

  /**
   * Say where the machine now stands, as distance, bearing and eye drop from the head (M13-11).
   *
   * Development only, and deliberately not a world coordinate: the run sheet reads these three
   * numbers off the console, and they are the three a renumbered tracking space cannot lie about.
   */
  private sayPlacement(what: string, tag = '[build]'): void {
    if (!import.meta.env.DEV) return
    const head = this.lastHead
    if (head === undefined) return
    const r = headRelative(this.rig.placement, head, this.benchCabinet())
    const facing = (r.facing * 180) / Math.PI
    this.say(
      `${tag} ${what}: near edge ${r.nearEdge.toFixed(3)} m, bearing ${((r.bearing * 180) / Math.PI).toFixed(1)}°, `
        + `bar ${r.barDrop.toFixed(3)} m below the eyes, scale ${this.rig.placement.scale.toFixed(2)}`
        + `, front turned ${facing.toFixed(1)}° from facing you`,
    )
  }

  /**
   * The first placement, said in the three numbers a renumbered space cannot lie about (XR-002c:
   * "a path that moves the machine logs where it ended up relative to the player").
   */
  private sayStood(): void {
    this.sayPlacement('stood in front of the player', '[xr]')
  }

  /** Frames since the last `[xr] hands` line. */
  private handsSaidAgo = 0

  /**
   * A development line about the hands, once a second and on every change of the menu.
   *
   * **The second device day's evidence problem, one layer down.** The DevTools socket carries the
   * page's console and the logcat carries the runtime's session states, and neither says what the
   * *headset handed the page*: which input sources, with joints or a gamepad or neither, and what
   * the left palm's dot with world up was while an author held it up and nothing opened. This is
   * that line. Costs nothing in production: `say` is inert there.
   */
  private sayHands(input: XRFrameInput, menuChanged: boolean): void {
    if (!import.meta.env.DEV) return
    this.handsSaidAgo += 1
    if (!menuChanged && this.handsSaidAgo < 72) return
    this.handsSaidAgo = 0
    const roster = [...input.inputSources]
      .map((source) => {
        const joints = source.hand !== undefined && source.hand !== null
        const pad = source.gamepad !== undefined && source.gamepad !== null
        return `${source.handedness}:${source.targetRayMode ?? '?'}:${joints ? 'joints' : pad ? 'gamepad' : 'bare'}`
      })
      .join(' ')
    const hand = (h: HandReport): string =>
      `${h.present ? h.kind : 'absent'}${h.grab ? ' pinch' : ''}${h.pinchTight ? ' tight' : ''}${h.closed ? ' fist' : ''}`
      + (h.present && h.kind === 'hands'
        ? ` ${Math.round(h.lastPinchMetres * 1000)}mm/${h.grabSource === 'runtime' ? 'evt' : h.grabSource}`
        : '')
    const left = this.hands.left
    const head = input.head
    const reach =
      head === undefined || !left.present || left.kind !== 'hands'
        ? ''
        : ` (${Math.hypot(left.palm.x - head.x, left.palm.y - head.y, left.palm.z - head.z).toFixed(2)} m from the eyes)`
    const refused = this.whyNotAMenu === undefined ? '' : ` [not a menu: ${this.whyNotAMenu}]`
    const palm = input.palmUp === undefined ? 'none' : `${input.palmUp.toFixed(2)}${reach}${refused}`
    this.say(
      `[xr] hands: L ${hand(this.hands.left)} | R ${hand(this.hands.right)} | palmUp ${palm} | menu ${this.menu.open ? 'open' : 'closed'}`
        + ` | sources ${roster || 'none'} | head ${input.head === undefined ? 'none' : `${input.head.y.toFixed(2)} m`}`
        + ` | floor ${input.floorY === undefined ? 'none' : `${input.floorY.toFixed(3)} m`}`
        // What is drawn (15 Sep 2026: "hands not showing, plunger range not showing"): the ghost's
        // joints seen per hand, and what the control picture found under the cabinet.
        + ` | ghost ${this.ghost === undefined ? 'none' : `L ${jointsSeen(this.joints.left)} R ${jointsSeen(this.joints.right)}`}`
        + ` | picture ${this.picture === undefined ? 'none' : this.picture.found}`,
    )
  }

  /**
   * A development line, to the console.
   *
   * **Read off the headset through the Quest Browser's DevTools socket** —
   * `scripts/quest-console.mjs`, which replays what the page has already said and then listens —
   * not through a beacon to the dev server. The beacon was the first answer (2026-09-13, after a
   * report that arrived with no evidence), and `scripts/check-anonymous.mjs` refused it for the
   * right reason: a beacon on the front door is a request from a visitor who agreed to nothing,
   * whatever the environment guard around it says. The socket needs no code on this side at all,
   * which is the better shape for an instrument.
   */
  private say(line: string): void {
    if (!import.meta.env.DEV) return
    console.info(line)
  }

  /**
   * Let go of a part without placing it, and put the picture right: no ghost, and the plinth it
   * came from lit again. Every path that cancels a carry goes through here — a method switch, the
   * headset coming off, a recycle, undo, redo, a test beginning — because a plinth left dark and a
   * ghost left standing are two ways for the tray to say the part is somewhere it is not.
   */
  private dropTheCarry(): void {
    const source = this.build.cancelGrabs()
    this.options.build?.ghost(undefined)
    this.unhover()
    this.unguide()
    this.calloutLit = undefined
    if (source?.kind === 'new') this.tray?.lift(source.plinth, false)
  }

  /** The placed part currently lit as under a fingertip, by id. */
  private hoverNow: string | undefined

  /** Whether the guides are up, so taking them down is one call and not one per frame (M13-13). */
  private guidesUp = false

  /** Whether the overlay is up (M13-17). */
  private validationUp = false

  /** Whether the card is up (M13-22). */
  private inspectorUp = false

  /** Whether the mini-map is up (M13-23). */
  private mapUp = false

  /** Whether the ramp handles are up (M13-24). */
  private pointsUp = false

  /** The test page's rows and the placing pinch (M13-25). */
  private readonly tools = new TestTools()

  /** Take the ramp handles down — on leaving, on a test, on a recycle. */
  private unshape(): void {
    this.build.shaper.release()
    if (!this.pointsUp) return
    this.pointsUp = false
    this.options.build?.points?.(undefined, 1)
  }

  /** Take the mini-map down — on leaving, on a test, on a recycle. */
  private unmap(): void {
    this.build.nudges.release()
    if (!this.mapUp) return
    this.mapUp = false
    this.options.build?.map?.(undefined, 1)
  }

  /** Take the card down — on leaving, on a test, on a recycle. */
  private uninspect(): void {
    this.build.inspector.release()
    if (!this.inspectorUp) return
    this.inspectorUp = false
    this.options.build?.inspector?.(undefined, 1)
  }

  /** The part a tapped callout lit, and until when — two seconds, then the hover has it back (M13-17). */
  private calloutLit: { readonly deviceId: string; readonly until: number } | undefined

  /** Take the overlay down — on leaving, on a test, on a recycle. */
  private unvalidate(): void {
    this.calloutLit = undefined
    this.build.callouts.release()
    if (!this.validationUp) return
    this.validationUp = false
    this.options.build?.validation?.(undefined, 1)
  }

  /** The bench's own haptics (M13-13): the tick a controller gets where a hand gets a click. */
  private readonly buildHaptics = { left: new Haptics(), right: new Haptics() }

  /** Take the guides down — on leaving, on a test, on a recycle: every path the ghost comes down on. */
  private unguide(): void {
    if (!this.guidesUp) return
    this.guidesUp = false
    this.options.build?.guides?.(undefined, 0, false)
  }

  /** Take the halo down — on leaving, on a test, on a recycle: every path the ghost comes down on. */
  private unhover(): void {
    if (this.hoverNow === undefined) return
    this.hoverNow = undefined
    this.options.build?.highlight?.(undefined)
  }

  /**
   * Everything a hand was holding, let go — the rig's gates and plunger, and the cabinet carry's
   * latches (2026-09-13). `releaseAll` alone left `CabinetGrab` remembering the hands as it last
   * saw them *before* Build mode, and the mode ends on a pinch — the wrist row is pressed with one
   * — so the first rig frame after Leave build read that pinch as a closing edge and, with the
   * full-size cabinet just restored around the author's hands, carried it off with the hand.
   */
  private letGoOfEverything(): void {
    this.rig.releaseAll()
    this.rig.grab.release()
    this.rig.spring.settle()
  }

  /**
   * This author's believed eye height, the way `reposition` resolves it: the head where the floor
   * is believable, the remembered height where it is not.
   */
  private believedEye(head: { readonly y: number }): number {
    return believableFloor(head.y) ? head.y : knownEyeHeight(this.storage)
  }

  /** The wrist row: shrink the machine onto the bench, raise the tray, hand the hands over. */
  private enterBuild(): void {
    const host = this.options.build
    if (host === undefined || !this.buildOffered || this.build.active) return
    const head = this.lastHead
    if (head === undefined) {
      this.toast = 'Not sure where you are yet — try again in a moment.'
      return
    }
    const before = this.rig.placement
    // The language the bench opens in (M13-26); read again every frame in `advanceBuild`.
    this.build.language = host.language?.() ?? 'en'
    // The bench replaces the machine the player was facing, square to it and in front of the
    // head — the press is made looking at the wrist, and the head's forward at that moment is the
    // direction of the left hand (14 Sep 2026); the line to the machine's centre tilts the bench
    // by however far the head is off that machine's centre line (26 Sep 2026).
    const from = benchFrom(head, before)
    const bench = this.build.enter(host.definition(), from.head, this.benchCabinet(), this.believedEye(head), from.toward)
    if (bench === undefined) return
    this.buildReturn = before
    this.benchAtEntry = { ...bench }
    host.enter()
    const editable = this.build.draft?.definition
    if (editable !== undefined && editable !== host.definition()) host.adopt(editable, true)
    // Reaching stops mid-gesture, as it does when comfort mode switches (`setComfort`).
    this.letGoOfEverything()
    this.rig.placement = bench
    this.tray = buildPartsTray(this.build.strings)
    this.options.scene.add(this.tray.group)
    this.poseTray()
    this.menu.home = 'build'
    this.build.entered()
    // One machine per Build session (M13-20): the first Keep creates it, the rest write it again.
    this.keep = new KeepSession()
    this.toast = 'Build mode. Tap a tile, pinch a part, drop it on the playfield.'
    this.completeNormalTask('enter-builder')
    this.sayPlacement('bench')
    if (import.meta.env.DEV) {
      const r = headRelative(before, head, this.benchCabinet())
      const deg = ((r.bearing * 180) / Math.PI).toFixed(1)
      this.say(
        `[build] the machine was ${deg}° off the head's forward at the press; the bench stands ${from.toward === undefined ? 'straight ahead' : 'square on the machine\'s centre line'}`,
      )
    }
  }

  /** The tray hangs over the lockdown bar, in the machine's own facing, at the machine's scale. */
  private poseTray(): void {
    const tray = this.tray
    if (tray === undefined) return
    const machine = this.options.machine
    machine.updateWorldMatrix(true, false)
    const dims = this.options.dimensions
    // The bar's centre in the cabinet's frame — front is local +Z — through the machine's own
    // matrix, so the tray sits on the *drawn* bar whatever the scale and the spring have done.
    const bar = machine.localToWorld(this.scratch.set(0, dims.lockdownBarHeight, dims.length / 2))
    tray.group.position.copy(bar)
    tray.group.rotation.set(0, machine.rotation.y, 0)
    tray.group.updateMatrixWorld(true)
    // The remove tray hangs off the drawn cabinet's right side (the owner, 24 Oct 2026): the side's
    // distance from the bar's centre, in the tray's own room metres, whatever the bench's scale.
    const side = machine.localToWorld(this.scratch.set(dims.width / 2, dims.lockdownBarHeight, dims.length / 2))
    tray.placeBin(Math.abs(tray.group.worldToLocal(side).x))
  }

  /**
   * One frame of Build mode: the tray's taps, both hands in the playfield's frame, the carry, the
   * ghost, and the commit (M13-07 … M13-10).
   */
  private advanceBuild(input: XRFrameInput, frameSeconds: number): void {
    const host = this.options.build
    const tray = this.tray
    if (host === undefined || tray === undefined) return
    this.buildHeartbeat(frameSeconds)
    host.advanceDrops?.(frameSeconds)
    // The player's language, as of this frame (M13-26): a change re-words the next card, the
    // next callout and the next toast; the tray's tiles are re-painted on their next frame.
    const language = host.language?.() ?? 'en'
    if (language !== this.build.language) {
      this.build.language = language
      tray.reword(this.build.strings)
    }

    // A renumbered space: the bench is re-derived from the first head this session reports.
    if (this.benchStale && input.head !== undefined) {
      this.benchStale = false
      const bench = this.build.rebench(input.head, this.benchCabinet(), this.believedEye(input.head))
      // **A test with a hand on the plunger stays where it stood** (25 Sep 2026): the bench is
      // re-derived for Back to build, and the machine under test keeps its relation to the head.
      const kept = this.build.phase === 'testing' ? this.testKept : undefined
      if (kept !== undefined) this.rig.placement = kept
      else if (bench !== undefined) this.rig.placement = bench
      this.testKept = undefined
    }
    this.poseTray()

    const trayHand = (hand: HandReport): TrayHand => ({
      handedness: hand.handedness,
      present: hand.present,
      x: hand.tip.x,
      y: hand.tip.y,
      z: hand.tip.z,
    })
    // The plinths give way to a part's card (26 Sep 2026): the two share the space over the near
    // playfield, and the parts on the plinths stood in front of the card.
    tray.stow(this.inspectorUp)
    // The history buttons say what they would change (the 1.0.115 round).
    tray.history(this.unfinishedBuildEdit()?undefined:this.build.undoWhat, this.unfinishedBuildEdit()?undefined:this.build.redoWhat)
    const trayFrame = tray.advance([trayHand(this.hands.left), trayHand(this.hands.right)])
    if (trayFrame.action !== undefined) {
      const what = trayFrame.action === 'undo' ? this.build.undoWhat : this.build.redoWhat
      this.dropTheCarry()
      const changed = trayFrame.action === 'undo' ? this.build.undo() : this.build.redo()
      if (changed) {
        this.adoptDraft()
        this.say(`[build] tray: ${trayFrame.action} ${what ?? ''}`)
        return
      }
    }
    const draft = this.build.draft
    const definition = host.definition()
    const playfieldSize = definition.dimensions.playfield
    if (trayFrame.tapped !== undefined && draft !== undefined) {
      // A tile tapped while a card is up asks for the plinths: the card goes, and they come back.
      if (this.inspectorUp && this.build.deselect()) tray.stow(false)
      tray.open(trayFrame.tapped, playfieldSize, devicesOf(draft))
    }

    // Hands in the playfield's own frame, scale divided out: PH-001 metres, the draft's metres.
    const playfield = host.playfield()
    playfield.updateWorldMatrix(true, false)
    // The carry is made with the pinch point, not the fingertip (`HandReport.pinch`): a part is
    // taken where the fingers meet, and the ghost rides between them. Taps stay on the fingertip.
    const carryBoard = { board: boardFor(playfieldSize, definition.dimensions.glassHeight, this.rig.placement.scale), playfield: playfieldSize, scale: this.rig.placement.scale, symmetry: false }
    const carryHand = (hand: HandReport, at: TrayPlinth | undefined, atTray: boolean): CarryHand => {
      // The bench's own pinch (`PinchCarry`): a release at a relaxed hand, landing where it began —
      // over the board only (24 Oct 2026, the native 1.0.210 log): off it there is nothing to land,
      // and a pinch loosening on the way down into the remove tray left the part at the rim.
      const bench = this.benchHand(hand)
      let local = playfield.worldToLocal(this.scratch.set(bench.x, bench.y, bench.z))
      if (hand.present && (bench.x !== hand.pinch.x || bench.y !== hand.pinch.y || bench.z !== hand.pinch.z)
        && !carriedOver(local.x, local.y, local.z, carryBoard)) {
        local = playfield.worldToLocal(this.scratch.set(hand.pinch.x, hand.pinch.y, hand.pinch.z))
      }
      return {
        handedness: hand.handedness,
        present: hand.present,
        x: local.x,
        y: local.y,
        z: local.z,
        // The menu wins the pointing hand: a pinch over a wrist row is a press, not a carry.
        grab: bench.grab && !(this.menuEngaged && hand.handedness === 'right'),
        atPlinth: at === undefined ? undefined : { index: at.index, kind: at.kind, refusal: at.refusal, ...(at.hand === undefined ? {} : { hand: at.hand }) },
        // At the shelf: a placed part let go here comes off the machine (`PartCarry`).
        atTray,
      }
    }
    const hands = [
      carryHand(this.hands.left, trayFrame.at.left, trayFrame.atTray.left),
      carryHand(this.hands.right, trayFrame.at.right, trayFrame.atTray.right),
    ]
    // Fingertips, for what is tapped rather than pinched: the card's steppers (M13-22) inside
    // the mode, the callouts (M13-17) after it.
    const tipHand = (hand: HandReport, at: TrayPlinth | undefined): CarryHand => {
      const local = playfield.worldToLocal(this.scratch.set(hand.tip.x, hand.tip.y, hand.tip.z))
      return {
        handedness: hand.handedness,
        present: hand.present,
        x: local.x,
        y: local.y,
        z: local.z,
        grab: this.benchHand(hand).grab,
        atPlinth: at === undefined ? undefined : { index: at.index, kind: at.kind, refusal: at.refusal, ...(at.hand === undefined ? {} : { hand: at.hand }) },
      }
    }
    const tips = [tipHand(this.hands.left, trayFrame.at.left), tipHand(this.hands.right, trayFrame.at.right)]
    const frame = this.build.advance(
      hands,
      {
        board: boardFor(playfieldSize, definition.dimensions.glassHeight, this.rig.placement.scale),
        playfield: playfieldSize,
        scale: this.rig.placement.scale,
        frameMs: frameSeconds * 1000,
      },
      tips,
    )
    const carry = frame.carry
    if (carry !== undefined) {
      if (carry.took && carry.source?.kind === 'new') tray.lift(carry.source.plinth, true)
      if (carry.dropped && carry.source?.kind === 'new') tray.lift(carry.source.plinth, false)
      if (carry.refused !== undefined) {
        this.toast = carry.refused
        this.say(`[build] refused: ${carry.refused}`)
      }
      if (carry.took) this.say(`[build] took ${carry.source?.kind === 'new' ? carry.source.device : carry.source?.deviceId ?? '?'} with the ${carry.held ?? '?'} hand`)
      if (carry.joined) this.say(`[build] both ends of ${carry.source?.kind === 'moved' ? carry.source.deviceId : '?'}: the ${carry.second ?? '?'} hand took the other`)
      if (carry.leaving === true && carry.source?.kind === 'moved') {
        this.toast = benchText(this.build.strings, 'toast.partLeaving', { name: carry.source.deviceId })
        this.say(`[build] ${carry.source.deviceId} at the tray — a release takes it off`)
      }
      if (carry.removed !== undefined) {
        // Into the box, not out of existence (the owner on the native 1.0.211): the part in hand
        // falls to the box's floor, rests, and fades.
        if (host.dropGhost?.((lowest) => binRest(tray.bin, lowest, 0.012)) === true) this.say(`[build] ${carry.removed} dropped into the remove tray`)
        this.toast = benchText(this.build.strings, 'toast.partRemoved', { name: carry.removed })
        this.say(`[build] removed ${carry.removed}`)
      } else if (carry.dropped) this.say(`[build] dropped: ${carry.committed ? 'placed' : 'not placed'}${carry.missed === undefined ? '' : ` (${carry.missed})`}`)
      // A part that went back because of where the hand was says why (the 1.0.113 round).
      if (carry.missed !== undefined) this.toast = missedLine(carry.missed, this.build.strings)
      // Off the board, the placed part in hand rides the hand (24 Oct 2026), so it can be seen
      // going into the remove tray rather than stopping at the board's edge.
      if (carry.ghost === undefined && carry.inHand !== undefined) host.ghost(carry.inHand, carry.inHandShift)
      else host.ghost(carry.ghost)
      host.lift?.(carry.held !== undefined && carry.source?.kind === 'moved' ? carry.source.deviceId : undefined)
      // What the snap looks like, and the sound-and-light twin on a rule change (M13-13): the
      // marks flash on both clients; a hand hears a click at the point, a controller feels a
      // twelve-millisecond tick. The grid is silent, and a rule letting go is only the guides
      // going dark.
      if (frame.guides !== undefined || this.guidesUp) {
        this.guidesUp = frame.guides !== undefined
        host.guides?.(frame.guides, frameSeconds, frame.ruleChanged)
      }
      if (frame.ruleChanged && frame.guides !== undefined) {
        const holder = carry.held === undefined ? undefined : this.hands[carry.held]
        this.buildHaptics.left.attach(this.hands.left.actuator)
        this.buildHaptics.right.attach(this.hands.right.actuator)
        if (holder !== undefined && holder.kind === 'controllers' && carry.held !== undefined) {
          this.buildHaptics[carry.held].fire(SNAP_PULSE)
        } else {
          host.click?.(frame.guides.at[0], frame.guides.at[1])
        }
        this.say(`[build] snap: ${frame.guides.label ?? 'grid'}`)
      }
      // The report on the machine (M13-17): drawn while editing, rebuilt by the picture when
      // its key changes, and a fingertip through a callout selects the part it names and says
      // the complaint — the Builder's click on a complaint, on the bench.
      if (frame.validation !== undefined || this.validationUp) {
        this.validationUp = frame.validation !== undefined
        host.validation?.(frame.validation, this.rig.placement.scale)
      }
      // The card beside the selected part (M13-22): drawn while a part is selected and none is
      // in hand, rebuilt by the picture as its rows change; what its steppers and rails wrote
      // this frame is the toast — *Skirt radius: 33 mm* — and a say line M13-11 can read.
      if (frame.inspector !== undefined || this.inspectorUp) {
        this.inspectorUp = frame.inspector !== undefined
        host.inspector?.(frame.inspector, this.rig.placement.scale, this.build.inspector.heldPath)
      }
      // The mini-map (M13-23): drawn while editing, the plan repainted as the machine changes; a
      // tap on an arrow is said, and a refused one is the toast.
      if (frame.map !== undefined || this.mapUp) {
        this.mapUp = frame.map !== undefined
        host.map?.(frame.map, this.rig.placement.scale)
      }
      // The selected ramp's points (M13-24): handles while a ramp is selected and no part is in
      // hand, the curve while a point is; what the hands did to the ramp is said, and a refused
      // move is the toast.
      if (frame.points !== undefined || this.pointsUp) {
        this.pointsUp = frame.points !== undefined
        host.points?.(frame.points, this.rig.placement.scale)
      }
      const shaped = frame.shaped
      if (shaped.chose !== undefined) this.say(`[build] chose ${shaped.chose}`)
      if (shaped.added !== undefined) {
        this.toast = benchText(this.build.strings, 'toast.pointAdded')
        this.say(`[build] added ${shaped.added.layerId} point ${shaped.added.index} with the ${shaped.added.hand} hand`)
      } else if (shaped.took !== undefined) {
        this.say(`[build] took ${shaped.took.layerId} point ${shaped.took.index} with the ${shaped.took.hand} hand`)
      }
      if (shaped.leaving !== undefined) {
        this.toast = benchText(this.build.strings, 'toast.pointLeaving')
        this.say(`[build] ${shaped.leaving.layerId} point ${shaped.leaving.index} past the edge — a release takes it off`)
      }
      if (shaped.removed !== undefined) {
        this.toast = benchText(this.build.strings, 'toast.pointRemoved')
        this.say(`[build] removed ${shaped.removed.layerId} point ${shaped.removed.index}`)
      } else if (shaped.dropped !== undefined) {
        const point = this.build.draft?.definition.layers.find((l) => l.id === shaped.dropped?.layerId)
        const at = point !== undefined && 'spline' in point ? point.spline[shaped.dropped.index] : undefined
        this.say(`[build] dropped ${shaped.dropped.layerId} point ${shaped.dropped.index}${at === undefined ? '' : ` at ${at.map((v) => (v * 1000).toFixed(1)).join(', ')} mm`}`)
      }
      if (shaped.refused !== undefined) {
        this.toast = shaped.refused
        this.say(`[build] refused: ${shaped.refused}`)
      }
      if (frame.picked !== undefined) {
        const p = frame.picked
        const where = `${(p.at[0] * 1000).toFixed(0)}, ${(p.at[1] * 1000).toFixed(0)} mm`
        this.say(p.deviceId === undefined ? `[build] map: nothing at ${where}` : `[build] map: chose ${p.deviceId} (tap at ${where})`)
      }
      if (frame.nudged !== undefined) {
        const n = frame.nudged
        const way = n.dx !== 0 ? `x${n.dx > 0 ? '+' : '-'}` : `y${n.dy > 0 ? '+' : '-'}`
        if (n.refused !== undefined) this.toast = n.refused
        this.say(`[build] nudge ${n.deviceId} ${way}${n.mm} mm${n.refused === undefined ? '' : ` refused — ${n.refused}`}`)
      }
      for (const edit of frame.edits) {
        this.toast = edit.refused ?? benchText(this.build.strings, 'toast.set', { label: edit.label, text: edit.text })
        this.say(
          edit.refused === undefined
            ? `[build] inspector: ${edit.kind} ${edit.deviceId}.${edit.path} → ${edit.text}`
            : `[build] inspector: ${edit.kind} ${edit.deviceId}.${edit.path} refused — ${edit.refused}`,
        )
      }
      const tapped = this.build.tapCallout(tips, this.rig.placement.scale)
      if (tapped !== undefined) {
        this.toast = tapped.message
        this.say(`[build] callout: ${tapped.label} (${tapped.code}) on ${tapped.deviceId ?? 'the playfield'} — ${tapped.message}`)
        this.calloutLit = tapped.deviceId === undefined ? undefined : { deviceId: tapped.deviceId, until: this.elapsed + CALLOUT_LIT_S }
        if (tapped.layerId !== undefined) this.say(`[build] chose ${tapped.layerId}`)
      }
      if (this.calloutLit !== undefined && this.elapsed >= this.calloutLit.until) this.calloutLit = undefined
      // The part a pinch would take, lit while the finger is over its handle (M13-13) — or the
      // part a tapped callout named, for a moment (M13-17). Said once per part, so the console
      // can confirm a hover the author did not notice.
      const hover = carry.hover?.deviceId ?? this.calloutLit?.deviceId
      if (hover !== this.hoverNow) {
        this.hoverNow = hover
        host.highlight?.(hover)
        if (hover !== undefined) this.say(`[build] over ${hover} (${carry.hover?.role ?? '?'}) with the ${carry.hover?.hand ?? '?'} hand`)
      }
    }
    if (frame.changed) this.adoptDraft()
  }

  // -------------------------------------------------------------------------------------
  // Keep (M13-20)
  //
  // THE ORIGINAL IS THE BUILDER'S OWN SAVE: `createMachine → saveVersion` with idempotency keys,
  // which `keepDraft` runs against the client the host's port answers with — the server records
  // `saved_by: webxr` from the meta header the client already sends in a headset session. The
  // draft on the bench is what is kept, retitled "<title> remix" for the reason `keeping.ts`
  // gives. The call goes out from the press and its one sentence comes back on a frame
  // (`sayWhatWasKept`), because the toast is a per-frame thing. Nothing is kept twice for one
  // press, and a Keep that fails leaves the bench exactly as it was. The native client did this
  // first (`MachinePreview.KeepTheDraft`, 19 Sep 2026); this is its mirror.
  // -------------------------------------------------------------------------------------

  /** The wrist row: the draft as it stands, off to the arcade. */
  /** The confirmed Reset (the 1.0.115 round): the machine as Build began, one step Undo takes back. */
  private resetTheDraft(): void {
    this.dropTheCarry()
    if (!this.build.reset()) {
      this.toast = this.build.strings['toast.nothingToReset']
      return
    }
    this.adoptDraft()
    this.toast = this.build.strings['toast.reset']
    this.say('[build] reset: the machine as Build began; undo brings the work back')
  }

  private keepTheDraft(): void {
    const draft = this.build.draft
    const session = this.keep
    const strings = this.build.strings
    if (!this.build.active || draft === undefined || session === undefined) {
      this.toast = strings['keep.nothing']
      return
    }
    if (this.keeping) {
      this.toast = strings['keep.busy']
      return
    }
    const port = this.options.build?.keep
    if (port === undefined) {
      this.toast = strings['keep.offline']
      this.say('[build] keep: this build has no backend; nothing sent')
      return
    }
    // The draft as it stands on this frame; a carry in flight is not part of it (the draft is a
    // value, and a drag writes nothing until it is dropped).
    const definition = draft.definition
    this.keeping = true
    this.keepingDefinition = definition
    this.toast = strings['keep.keeping']
    this.say(
      `[build] keep: sending the draft (${devicesOf(draft).length} device(s)) as `
        + (session.machineId === undefined ? `a new machine, “${keepTitleFor(definition)}”` : `“${session.title ?? ''}” ${session.semver}`),
    )
    void port()
      .then((backend) => keepDraft(backend, definition, session, strings))
      .catch(
        (error: unknown): KeepOutcome => ({
          kept: false,
          says: refusedLine(strings['keep.wentWrong'], strings),
          log: `keep: threw ${error instanceof Error ? error.message : String(error)}`,
          warnings: 0,
        }),
      )
      .then((outcome) => {
        this.keptSaid = outcome
      })
  }

  /** The Keep that finished since last frame, said once, on the frame. */
  private finishBuilderDeparture(): void {
    const pending = this.pendingDeparture
    if (pending === undefined || this.keeping) return
    this.pendingDeparture = undefined
    this.consumeMenuPointer(pending.button)
    this.leaveBuild()
    if (pending.selection.item !== 'leaveBuild') this.onMenuSelection(pending.selection, pending.button)
  }

  private sayWhatWasKept(): void {
    const outcome = this.keptSaid
    if (outcome === undefined) return
    this.keptSaid = undefined
    this.keeping = false
    const saved = this.keepingDefinition
    if (outcome.kept) this.keptDefinition = saved
    this.keepingDefinition = undefined
    if (this.pendingDeparture?.saving !== undefined) {
      const same = this.pendingDeparture.saving === saved && saved === this.build.draft?.definition
      this.pendingDeparture.saving = undefined
      if (outcome.kept && same && !this.unfinishedBuildEdit()) this.finishBuilderDeparture()
      else this.menuFeedback = outcome.kept ? 'An earlier revision was kept. Your current draft stays here.' : outcome.says
    }
    this.toast = outcome.says
    this.say(
      outcome.kept
        ? `[build] keep: kept “${outcome.title ?? ''}” ${outcome.semver ?? ''} (${outcome.machineId ?? ''}), ${outcome.warnings} warning(s); ${outcome.log}`
        : `[build] keep: not kept: ${outcome.says}; ${outcome.log}`,
    )
  }

  /**
   * Every five seconds of editing, the frame times as a distribution — a median and a worst,
   * never a minimum, which converges to nothing after an hour of use (M13-11). Development only.
   */
  private buildHeartbeat(frameSeconds: number): void {
    if (!import.meta.env.DEV) return
    this.buildFrames.push(frameSeconds * 1000)
    this.buildHeartbeatAt += frameSeconds
    if (this.buildHeartbeatAt < 5) return
    const sorted = [...this.buildFrames].sort((a, b) => a - b)
    const median = sorted[Math.floor(sorted.length / 2)] ?? 0
    const worst = sorted[sorted.length - 1] ?? 0
    const keep = this.keep
    this.say(
      `[build] frames: median ${median.toFixed(1)} ms, worst ${worst.toFixed(1)} ms over ${sorted.length}, `
        + `phase ${this.build.phase}`
        + (this.keeping ? ', keeping' : keep?.machineId !== undefined ? `, kept as ${keep.slug ?? ''} ${keep.semver} ×${keep.kept}` : ''),
    )
    this.buildFrames.length = 0
    this.buildHeartbeatAt = 0
  }

  /** The draft is the machine now: the session rebuilds on it and the tray's refusals are re-read. */
  private adoptDraft(): void {
    const host = this.options.build
    const draft = this.build.draft
    const tray = this.tray
    if (host === undefined || draft === undefined) return
    const began = performance.now()
    try {
      host.adopt(draft.definition, this.build.canUndo)
    } catch (error) {
      // **A throw here is a frozen headset**: Three's animation loop requests the next frame
      // *after* the callback returns, so an exception in it ends rendering for the session. The
      // carry already refuses a release the validator rejects; this is the net under it, for a
      // draft the loader refuses for a reason the validator did not name. The commit is taken
      // back and the previous draft — which loaded — is adopted again.
      const message = error instanceof Error ? error.message : String(error)
      this.say(`[build] the session refused the draft: ${message}`)
      this.toast = 'Not placed — the machine would not load. Taken back.'
      if (this.build.undo()) {
        try {
          host.adopt(this.build.draft?.definition ?? draft.definition, this.build.canUndo)
        } catch {
          // The previous draft loaded once and loads again; nothing further to do if not.
        }
      }
      return
    }
    // M13-11 reads this: the whole commit — the session rebuilt, the picture patched.
    this.say(`[build] adopted the draft in ${(performance.now() - began).toFixed(1)} ms`)
    // A rebuilt machine has new caps, lamps and a new shooter rod; the picture's cached ones are
    // orphans, and it finds the new ones on its next frame.
    this.picture = undefined
    // A bank becomes takeable the moment a drop target lands: the open category is re-read.
    if (tray !== undefined && tray.opened !== undefined) {
      tray.open(tray.opened, host.definition().dimensions.playfield, devicesOf(draft))
    }
  }

  /** Test play: the verdict, then 1:1 on the floor, or in place on the bench with a notice. */
  private beginTest(): void {
    const head = this.lastHead
    if (head === undefined) {
      this.toast = 'Not sure where you are yet — try again in a moment.'
      return
    }
    // A part in the hand when the machine grows is a part nobody placed — and its plinth is lit
    // again here, because `BuildMode.beginTest` cancels the carry without a tray to tell.
    if (this.build.carry.carrying) this.dropTheCarry()
    const decision = this.build.beginTest(head, this.benchCabinet(), {
      fit: this.ritual?.view().fit,
      eyeHeight: this.believedEye(head),
      floorY: this.venue?.group.position.y ?? 0,
    })
    if (decision === undefined) return
    this.toast = decision.says
    this.say(`[build] test: ${decision.outcome} — ${decision.says}`)
    if (decision.outcome === 'refused') return
    this.options.build?.ghost(undefined)
    this.unhover()
    this.unguide()
    this.unvalidate()
    this.uninspect()
    this.unmap()
    this.unshape()
    if (this.tray !== undefined) this.tray.group.visible = false
    this.letGoOfEverything()
    this.rig.placement = decision.placement
    // The test tools (M13-25): allowed on the draft's session from here, the test page home.
    this.tools.reset()
    this.options.build?.tools?.begin()
    this.testFrozenForMenu = false
    this.options.build?.tools?.ribbon(this.tools.trail)
    this.menu.home = 'test'
    this.sayPlacement(decision.outcome === 'full' ? 'test at full' : 'test on the bench')
  }

  /**
   * The placing pinch (M13-25), and the adding one (26 Sep 2026) which is the same pinch spending
   * the other arming: while Place ball is armed, the first pinch that closes over the
   * board — in the playfield's frame, which is the full-size machine's during a test — moves the
   * ball there, and says so. Nothing else of Build mode reads a hand during a test.
   */
  /** The bench's own pinch per hand (`PinchCarry`, the 1.0.106 round) and the still point it lands on. */
  private readonly benchPinch = { left: new PinchCarry(), right: new PinchCarry() }
  private readonly stillPinch = { left: { x: 0, y: 0, z: 0 }, right: { x: 0, y: 0, z: 0 } }

  /**
   * Advance both hands' bench pinch once a frame. Only where the grab is the thumb-to-index rule:
   * a runtime pinch (`selectstart`) and a controller's squeeze keep their own answer.
   */
  private advanceBenchPinch(): void {
    for (const hand of [this.hands.left, this.hands.right]) {
      const pinch = this.benchPinch[hand.handedness]
      if (!hand.present || hand.grabSource !== 'distance') {
        pinch.reset()
        continue
      }
      pinch.advance(hand.grabMeasured, hand.lastPinchMetres)
      if (!pinch.settling) {
        const still = this.stillPinch[hand.handedness]
        still.x = hand.pinch.x
        still.y = hand.pinch.y
        still.z = hand.pinch.z
      }
    }
  }

  /** A hand's grab and pinch point as the bench reads them. */
  private benchHand(hand: HandReport): { readonly grab: boolean; readonly x: number; readonly y: number; readonly z: number } {
    if (!hand.present || hand.grabSource !== 'distance') return { grab: hand.grab, x: hand.pinch.x, y: hand.pinch.y, z: hand.pinch.z }
    const pinch = this.benchPinch[hand.handedness]
    const at = pinch.settling ? this.stillPinch[hand.handedness] : hand.pinch
    return { grab: pinch.closed, x: at.x, y: at.y, z: at.z }
  }

  private advanceMenuStepRepeat(frameSeconds: number): void {
    const tools=this.options.build?.tools
    if(tools===undefined||this.build.phase!=='testing')return
    // The Step row held down while frozen repeats (polish): the press stepped once; after a
    // pause longer than a tap, ten more steps a second while the finger stays on the row.
    const holdingStep = this.menu.open && this.panel?.held() === 'step' && tools.frozen()
    const repeats = this.tools.stepRepeats(holdingStep, frameSeconds * 1000)
    for (let i = 0; i < repeats; i++) {
      if (!tools.step()) break
      this.toast = this.build.strings['toast.stepped']
    }
    if (repeats > 0) this.say(`[build] test tools: step +${repeats} ms (held)`)
  }

  private advanceTestTools(): void {
    const host = this.options.build
    const tools = host?.tools
    if (host === undefined || tools === undefined) return
    const definition = host.definition()
    const playfield = host.playfield()
    playfield.updateWorldMatrix(true, false)
    const hands: CarryHand[] = []
    for (const hand of [this.hands.left, this.hands.right]) {
      const bench = this.benchHand(hand)
      const local = playfield.worldToLocal(this.scratch.set(bench.x, bench.y, bench.z))
      hands.push({
        handedness: hand.handedness,
        present: hand.present,
        x: local.x,
        y: local.y,
        z: local.z,
        grab: bench.grab && !TestTools.menuMasks(this.menuEngaged, hand.handedness, this.tools.heldBy),
        atPlinth: undefined,
      })
    }
    // Aimed from above (the 1.0.103 round): a hand over the glass at full size is well past
    // `OVER_BOARD`, and the Place/Add ball pinch went nowhere. The raised board is the carry's.
    const scale = this.rig.placement.scale
    const placed = this.tools.advance(hands, raisedBoard(boardFor(definition.dimensions.playfield, definition.dimensions.glassHeight, scale), scale))
    if (placed === undefined) return
    const mm = (v: number): string => (v * 1000).toFixed(0)
    if (this.tools.added) {
      if (tools.addBall(placed[0], placed[1])) {
        this.toast = benchText(this.build.strings, 'toast.ballAdded', { x: mm(placed[0]), y: mm(placed[1]) })
        this.say(`[build] test tools: ball added at ${mm(placed[0])}, ${mm(placed[1])} mm`)
      } else {
        this.toast = this.build.strings['toast.noRoom']
        this.say('[build] test tools: no ball to add')
      }
      return
    }
    if (tools.placeBall(placed[0], placed[1])) {
      this.toast = benchText(this.build.strings, 'toast.ballPlaced', { x: mm(placed[0]), y: mm(placed[1]) })
      this.say(`[build] test tools: ball placed at ${mm(placed[0])}, ${mm(placed[1])} mm`)
    } else {
      this.toast = this.build.strings['toast.noBall']
      this.say('[build] test tools: no ball in play to place')
    }
  }

  /** Back to build: the bench, to the centimetre. */
  private endTest(): void {
    const bench = this.build.endTest()
    if (bench === undefined) return
    this.options.build?.tools?.end()
    this.tools.reset()
    this.menu.home = 'build'
    this.letGoOfEverything()
    this.rig.placement = bench
    if (this.tray !== undefined) this.tray.group.visible = true
    this.toast = 'Back on the bench.'
    this.completeNormalTask('return-to-editing')
    // The three head-relative numbers move with the head — the 18:58 session stood up and
    // stepped in during its test — so the byte-identity claim (M13-11) is said outright.
    const entry = this.benchAtEntry
    const same =
      entry !== undefined
      && entry.x === bench.x && entry.y === bench.y && entry.z === bench.z && entry.yaw === bench.yaw && entry.scale === bench.scale
    this.sayPlacement(`back on the bench — ${same ? 'identical to the bench you entered on' : 'NOT the bench you entered on'}`)
  }

  /** The bench as Build was entered on, for `endTest` to say whether the same one came back. */
  private benchAtEntry: Placement | undefined

  /** The mode ends: the tray goes, the original machine and its placement come back. */
  private leaveBuild(): void {
    if (!this.build.active) return
    const host = this.options.build
    // A test in flight ends with the mode (M13-25): the tools are refused before the original comes back.
    if (this.build.phase === 'testing') {
      host?.tools?.end()
      this.tools.reset()
    }
    this.build.leave()
    host?.ghost(undefined)
    host?.advanceDrops?.(0, true)
    this.unhover()
    this.unguide()
    this.unvalidate()
    this.uninspect()
    this.unmap()
    this.unshape()
    const tray = this.tray
    if (tray !== undefined) {
      this.options.scene.remove(tray.group)
      tray.dispose()
      this.tray = undefined
    }
    host?.leave()
    // The original machine is back, with its own cabinet: the picture finds it again.
    this.picture = undefined
    this.build.left()
    // The next Build session keeps into a machine of its own. A Keep still on the wire finishes
    // and is said — it is a fact about the arcade, whatever the bench is doing now.
    this.keep = undefined
    this.keptDefinition = undefined
    this.keepingDefinition = undefined
    const back = this.buildReturn
    this.buildReturn = undefined
    this.benchStale = false
    if (back !== undefined) this.rig.placement = back
    this.letGoOfEverything()
    // The ritual was frozen; its pinch latch closes with everything else, so the pinch that
    // pressed Leave build does not place a machine that has not been aimed.
    this.ritualPinchWas.left = true
    this.ritualPinchWas.right = true
    this.menu.home = 'root'
    this.toast = 'Left Build mode.'
    this.completeNormalTask('leave-builder')
    this.sayPlacement('left build mode')
  }

  private buildVenue(): void {
    if (this.venue !== undefined) return
    this.venue = buildArcadeRoom(this.floor.bays, this.floor.index)
    this.options.scene.add(this.venue.group as Group)
    // **Inside the venue group, not beside it.** The room is translated so the player's bay sits
    // at the origin (see `standAtBay`), and a teleport is one more translation of that group.
    // Avatars parented to it move with the room for free; parented to the scene they would slide
    // to the wrong bay the first time somebody teleported.
    this.avatars = buildAvatars(this.floor.bays)
    this.venue.group.add(this.avatars.group)
    this.standAtBay()
  }

  /**
   * Put the player's bay in front of them.
   *
   * **The room moves, not the player.** A WebXR reference space has its origin where the player
   * physically is, and there is no way to teleport a person; the only thing an application can do
   * is move the world underneath them. So the venue is translated so that the current bay's
   * standing spot sits at the origin, and the machine is placed in the same shifted frame.
   *
   * This is also what makes teleporting between bays (M5-23) free of a movement system: changing
   * bay is one translation of the venue group, applied between frames, with nothing animating and
   * nothing to feel sick about.
   */
  private standAtBay(head?: StandingHead): void {
    const bay = this.floor.bay
    // **At the player, not at the origin** (14 Sep 2026: "in VR mode pinball machine not in
    // right place"). The room used to be carried so that the bay's standing spot sat on the
    // reference space's origin — and the origin is wherever the runtime put it: on a Quest,
    // `local-floor`'s is the last system recentre, not the player, while the same head had the
    // machine placed exactly in MR. So the machine is stood where the mixed-reality ritual would
    // stand it — `placementInFrontOf` this head, along its forward, XR-002c — and the room is
    // turned and carried so that the player's bay is that machine. Every bay, avatar and pose
    // keeps its venue coordinates (`venueFrame.ts` converts); only where this player sees the
    // room changes. Before a head has arrived, the origin, as before.
    let placement: Placement =
      head === undefined
        ? { x: bay.x - bay.standX, y: 0, z: bay.z - bay.standZ, yaw: bay.yaw, scale: 1 }
        : placementInFrontOf(head.x, head.z, squareToRoom(head.forward.x, head.forward.z), 1, this.options.dimensions.length)
    if (head !== undefined && this.comfortOn && !this.build.active) {
      placement = seatedPlacement(placement, head.x, head.y, head.z,
        Math.atan2(head.forward.x, -head.forward.z), this.options.dimensions.lockdownBarHeight,
        this.options.dimensions.length, knownEyeHeight(this.storage), heightDropOffset(readHeightOffset(this.storage)), true)
    }
    this.standVenueAt(placement)
  }

  /** Turn and carry the room so that the player's bay is `placement`, and place the machine there. */
  private standVenueAt(placement: Placement): void {
    const bay = this.floor.bay
    const yaw = placement.yaw - bay.yaw
    const c = Math.cos(yaw)
    const s = Math.sin(yaw)
    // The bay's cabinet origin, turned by the room's yaw, lands on the placement.
    this.venuePose = { x: placement.x - (bay.x * c - bay.z * s), y: 0, z: placement.z - (bay.x * s + bay.z * c), yaw }
    this.venue?.group.position.set(this.venuePose.x, this.venuePose.y, this.venuePose.z)
    this.venue?.group.rotation.set(0, -yaw, 0)
    this.placeMachine(placement)
    this.rig.spring.settle()
  }

  /** The last head, as `standAtBay` wants it — or nothing, and the bay stands at the origin. */
  private lastStandingHead(): StandingHead | undefined {
    const head = this.lastHead
    return head === undefined ? undefined : standingHead(head)
  }

  /**
   * Teleport to the bay the player is looking at (M5-23).
   *
   * Returns true when the venue moved, so the caller can play the one sound it deserves. Silent
   * when the player is looking at nothing or already at the bay they aimed at — a teleport that
   * fires on the bay you are standing in is a control that appears to be broken.
   */
  teleport(head: { x: number; z: number; yaw: number }): boolean {
    if (this.venue === undefined) return false
    // The look test is in venue space, which is the room's own frame: the head is carried and
    // turned out of the reference space by the venue's pose (14 Sep 2026 — the room turns now).
    const at = toPlayfield({ x: head.x, y: 0, z: head.z }, this.venuePose)
    const aimed = bayLookedAt(this.floor.bays, at.x, at.z, yawToPlayfield(head.yaw, this.venuePose))
    const statuses = bayStatuses(this.floor.bays.length, this.floor.index, this.occupants())
    return this.goToBay(aimed === undefined ? undefined : statuses[aimed.index])
  }

  /**
   * Go to a bay, if the room has one to give (M8-20).
   *
   * **The refusal that says why is the only one worth saying.** Aiming at nothing and aiming at
   * the machine you are already at are both silence — a control that announces its own misfires is
   * one a player learns to distrust, and a teleport that fires on your own bay appears broken.
   * Aiming at a machine somebody is playing is the one case where the player did everything right
   * and nothing happened, so it gets a sentence with a name in it.
   *
   * The venue moves immediately and the room is *asked* rather than waited for: a quarter of a
   * second between a pinch and the room moving reads as a dropped input. `reconcileBay` is what
   * corrects the rare disagreement.
   */
  private goToBay(status: BayStatus | undefined): boolean {
    if (this.venue === undefined) return false
    const move = bayMove(status)
    if (move.kind === 'taken') {
      this.toast = takenLine(move.who)
      return false
    }
    if (move.kind === 'stay') return false
    if (!this.floor.goTo(move.index)) return false
    // The machine the player aimed at comes to stand in front of them, along the gaze that chose it.
    this.standAtBay(this.lastStandingHead())
    this.venue.setPlayerBay(this.floor.index)
    // **Announced, or this teleport is invisible.** A pose carries the bay it was composed
    // against and a receiver drops one that disagrees with the roster — so a client that moved
    // without telling the room freezes its own avatar on everybody else's screen.
    this.venuePresence?.moveTo?.(move.index)
    this.askedBay = move.index
    this.askedAt = this.elapsed
    this.toast = arrivalLine(move.index)
    return true
  }

  /**
   * Draw the other people, and tell them where this player is (M8-04, M8-05).
   *
   * Both halves are guarded by the venue existing rather than by the port existing, because both
   * are meaningless without a room: in MR there is no arcade and no bay, and 06 §4's presence is a
   * VR feature. The port itself decides how often a pose actually leaves — calling it every frame
   * is correct and costs a comparison.
   */
  private advanceVenuePresence(input: XRFrameInput): void {
    const avatars = this.avatars
    if (avatars === undefined) return
    const port = this.venuePresence
    avatars.update(port, this.elapsed)
    if (port === undefined || input.head === undefined) return

    // **In the venue's own frame, not the reference space's.** The room is carried and turned so
    // that this player's bay stands at their head (`standAtBay`), so the head's place in the
    // room is the reference-space pose taken back through the venue's pose — and it is the
    // room's frame that both clients share. Sending the reference-space position would stream
    // where the player is standing in *their* living room, which is meaningless to everybody
    // else and different every session.
    const pose = this.venuePose
    const at = toPlayfield(input.head, pose)
    const head: AvatarPose = {
      x: at.x,
      y: at.y,
      z: at.z,
      yaw: yawToPlayfield(input.head.yaw, pose),
      pitch: input.head.pitch ?? 0,
      roll: input.head.roll ?? 0,
    }
    // **Told before the poses are published, and with the same head.** The listener has to move
    // in the same frame the speakers do, or a player turning their head hears the room swing a
    // frame late — which at 72 Hz is not a delay anybody notices as a delay, it is a room that
    // feels slightly detached from them.
    port.hear?.(head)
    port.publish(
      this.floor.index,
      head,
      shift(input.wrists?.left ?? null, pose),
      shift(input.wrists?.right ?? null, pose),
    )
  }

  private teardownVenue(): void {
    const venue = this.venue
    if (venue === undefined) return
    this.avatars?.dispose()
    this.avatars = undefined
    this.options.scene.remove(venue.group as Group)
    for (const g of venue.geometries) g.dispose()
    for (const m of venue.materials) m.dispose()
    for (const t of venue.textures) t.dispose()
    this.venue = undefined
  }

  private report(confirmations: readonly ConfirmEvent[]): XRFrameReport {
    return {
      presenting: this.manager.presenting,
      mode: this.manager.mode,
      look: this.look,
      method: wireInputMethod(this.rig.method.method),
      confirmations,
      toast: this.toast,
      ritual: this.ritual?.view(),
      lighting: this.lighting,
      paused: this.pausedByVisibility,
    }
  }
}

/** A pose from reference space into the venue's frame. See `advanceVenuePresence`. */
function shift(pose: AvatarPose | null, venue: VenuePlacement): AvatarPose | null {
  if (pose === null) return null
  const at = toPlayfield(pose, venue)
  return { ...pose, x: at.x, y: at.y, z: at.z, yaw: yawToPlayfield(pose.yaw, venue) }
}

/** A head as a `VenuePlacement`, so `venueFrame.ts` can express a placement relative to it. */
function headPoseOf(head: { x: number; z: number; yaw: number }): VenuePlacement {
  return { x: head.x, y: 0, z: head.z, yaw: head.yaw }
}

/** Where the machine stands in the head's own frame (`rememberWhereItStands`). */
interface HeadRelation {
  readonly at: { x: number; y: number; z: number }
  readonly yaw: number
  readonly scale: number
}

/** `seen/25` for the hands line. */
function jointsSeen(hand: HandJoints): string {
  let n = 0
  for (let i = 0; i < HAND_JOINT_COUNT; i += 1) if (hand.seen[i] === 1) n += 1
  return `${n}/${HAND_JOINT_COUNT}`
}

/** What `standAtBay` stands the venue at: a head's floor position and level forward. */
interface StandingHead {
  readonly x: number
  readonly y: number
  readonly z: number
  readonly forward: LevelDirection
}

/** A frame's head as a `StandingHead`, the forward levelled the way the ritual levels it. */
function standingHead(head: { x: number; y: number; z: number; yaw: number; pitch?: number; forward?: { x: number; y: number; z: number } }): StandingHead {
  const f = head.forward ?? forwardFromYaw(head.yaw, head.pitch ?? 0)
  return { x: head.x, y: head.y, z: head.z, forward: levelForward(f.x, f.z) }
}

/** The `end` listener shape a session hands back. Re-exported so a shell can type its own. */
export type { XRSessionPort }

/**
 * The browser's own rigid transform, when there is one.
 *
 * Returns `undefined` where `XRRigidTransform` is absent — Node, and any browser without WebXR —
 * which `FloorAlignment` reads as "there is no correction to apply". That is the right answer
 * rather than a crash: a runtime with no rigid transforms has no offset reference spaces either,
 * so there was never a lever here to pull.
 */
function defaultRigidTransform(y: number): unknown {
  const ctor = (globalThis as { XRRigidTransform?: new (p: { x: number; y: number; z: number }) => unknown })
    .XRRigidTransform
  return ctor === undefined ? undefined : new ctor({ x: 0, y, z: 0 })
}
